// Libs
import React from 'react';
// Utils
import { filterDataByFilterOptions } from 'utils/filters';
import { ImportUtils } from 'utils/Orders/ImportProcess';
// Hooks
import { useOrderApi, useTheme } from 'hooks';
// Components
import { Drawer } from 'components';
import ZoneId from 'components/DataFieldManager/ZoneId/ZoneId';
import ServiceId from 'components/DataFieldManager/AssignedServiceId/AssignedServiceId';
import AssignedTechId from 'components/DataFieldManager/AssignedTechId/AssignedTechId';
import ContractFrequencyId from 'components/DataFieldManager/ContractFrequencyId/ContractFrequencyId';
import {
	applyContractFrequencySelection,
	getContractFrequencyPresentation,
	getContractFrequencyReviewSelectionsPayload,
	invalidateContractFrequencyCapabilities,
} from 'components/DataFieldManager/ContractFrequencyId/contractFrequencyReview.model';
import {
	areContractFrequencyCreationArtifactsSemanticallyEqual,
	buildContractFrequencyCreationRequest,
	getContractFrequencyCreationPlanFingerprint,
	getContractFrequencyCreationPlansFromOrders,
	getContractFrequencyCreationRequestsPayload,
	getContractFrequencyCreationSelectionsPayload,
	getContractFrequencyCreationSummary,
	invalidateContractFrequencyCreationCapabilities,
	invalidateContractFrequencyCreationCohorts,
	setContractFrequencyCreationSelected,
} from './contractFrequencyCreation.model';
import HeaderPreview from './HeaderPreview';
import TablePreview from './TablePreview';
import ImportReviewWorkspace from './ImportReviewWorkspace';
import {
	applyManualLocationSelection,
	applyManualServiceSelection,
	applyServiceCreationRequest,
	applyAssignmentReviewSnapshot,
	assignImportRowIdentities,
	clearZoneCreationSelection,
	getImportRowIdentity,
	getEffectiveOrderReviewState,
	getEditableZoneFields,
	getReviewSummary,
	getZoneCoverageChangeCount,
	getZoneCoverageChangeKeys,
	getZoneCreationOption,
	getZoneCreationReviewSnapshot,
	getZoneCreationSelectionsFromOrders,
	getZoneCreationSelectionsPayload,
	getZoneCreationSnapshotFingerprint,
	getZoneCreationSummary,
	isZoneCreationSelectionAllowed,
	getAssignmentDriftRowIds,
	getAssignmentReviewErrorState,
	getAssignmentReviewProofFingerprint,
	getAssignmentReviewSnapshot,
	getAssignmentReviewSelectionsPayload,
	getAssignmentSnapshotFingerprint,
	getImportCreationOutcome,
	getImportDiagnostics,
	getContractFrequencyReviewFingerprint,
	getFieldReviewDescriptors,
	getFieldReviewSelectionsPayload,
	getFieldReviewSnapshot,
	hasSafeFieldReviewSnapshot,
	normalizeFieldReviewValue,
	getAssignmentPresentation,
	hasSafeImportRowCorrelation,
	hasAssignmentApprovalRequired,
	getSourceValues,
	getServiceCohortKey,
	getServiceCohortTargets,
	getServicePresentation,
	isExistingImportOrder,
	getServiceCreationSelectionsFromOrders,
	getServiceCreationSelectionsPayload,
	getServiceCreationPlanFingerprint,
	getServiceReviewSelectionsPayload,
	isServiceIssueResolvedByCreation,
	isOrderTargetedByServiceCreation,
	mergeImportDiagnostics,
	markOrderForRevalidation,
	markAssignmentDrift,
	prepareImportOrdersForAssignment,
	requiresRevalidation,
	setServiceCreationSelected,
	undoManualServiceSelection,
	REVIEW_STATUS,
} from './importReview.model';
import { getImportReviewThemeStyle } from './importReview.theme';
import {
	getBulkAssignmentOptionKey,
	getBulkAssignmentOptions,
	getBulkContractFrequencyOptionKey,
	getBulkLocationOptionKey,
	getBulkLocationOptions,
	getBulkServiceOptionKey,
	getBulkServiceOptions,
	getSelectedImportOrders,
	prepareBulkContractFrequencyTransition,
} from './importBulkActions.model';
import styles from './ImportReview.module.css';

const hasOwn = (value, key) =>
	Object.prototype.hasOwnProperty.call(value || {}, key);

const captureContractFrequencyState = (order, field) => ({
	fields: Array.from(new Set([field?.id, field?.name].filter(Boolean))).map(
		fieldId => ({
			fieldId,
			present: hasOwn(order, fieldId),
			value: order?.[fieldId],
		}),
	),
	diagnostics: ['frequencyMatch', 'frequency_match'].map(key => ({
		key,
		present: hasOwn(order?.importDiagnostics, key),
		value: order?.importDiagnostics?.[key],
	})),
});

const restoreContractFrequencyState = (order, state) => {
	if (!state) return order;
	const restoredOrder = { ...order };
	(state.fields || []).forEach(field => {
		if (field.present) restoredOrder[field.fieldId] = field.value;
		else delete restoredOrder[field.fieldId];
	});
	const diagnostics = { ...getImportDiagnostics(restoredOrder) };
	(state.diagnostics || []).forEach(diagnostic => {
		if (diagnostic.present) diagnostics[diagnostic.key] = diagnostic.value;
		else delete diagnostics[diagnostic.key];
	});
	return { ...restoredOrder, importDiagnostics: diagnostics };
};

const restoreStagedFieldReviewValue = (order, artifact) => {
	const fieldId = artifact?.fieldId;
	if (!fieldId) return order;
	const restoredOrder = { ...order };
	if (
		hasOwn(artifact, 'previousValue') &&
		artifact.previousValue !== undefined
	) {
		restoredOrder[fieldId] = artifact.previousValue;
		return restoredOrder;
	}
	const sourceCategory = String(artifact?.sourceCategory || '').toLowerCase();
	if (sourceCategory === 'missing') {
		delete restoredOrder[fieldId];
		return restoredOrder;
	}
	if (sourceCategory === 'null') {
		restoredOrder[fieldId] = null;
		return restoredOrder;
	}
	if (sourceCategory === 'empty') {
		restoredOrder[fieldId] = '';
		return restoredOrder;
	}
	const sourceFieldValues = getImportDiagnostics(order).sourceFieldValues || {};
	if (hasOwn(sourceFieldValues, fieldId)) {
		restoredOrder[fieldId] = sourceFieldValues[fieldId];
		return restoredOrder;
	}
	if (hasOwn(artifact, 'previousValue')) delete restoredOrder[fieldId];
	return restoredOrder;
};

const restoreStagedFieldReviewValues = (order, artifacts) =>
	(artifacts || []).reduce(restoreStagedFieldReviewValue, order);

const restoreSignedSourceFieldReviewValue = (order, artifact) => {
	const fieldId = artifact?.fieldId;
	if (!fieldId) return order;
	const restoredOrder = { ...order };
	const sourceCategory = String(artifact?.sourceCategory || '').toLowerCase();
	if (sourceCategory === 'missing') {
		delete restoredOrder[fieldId];
		return restoredOrder;
	}
	if (sourceCategory === 'null') {
		restoredOrder[fieldId] = null;
		return restoredOrder;
	}
	if (sourceCategory === 'empty') {
		restoredOrder[fieldId] = '';
		return restoredOrder;
	}
	const sourceFieldValues = getImportDiagnostics(order).sourceFieldValues || {};
	if (hasOwn(sourceFieldValues, fieldId))
		restoredOrder[fieldId] = sourceFieldValues[fieldId];
	else delete restoredOrder[fieldId];
	return restoredOrder;
};

const restoreSignedSourceFieldReviewValues = (order, artifacts) =>
	(artifacts || []).reduce(restoreSignedSourceFieldReviewValue, order);

const clearFieldReviewDescriptors = (order, fieldIds) => {
	const diagnostics = getImportDiagnostics(order);
	const fieldReview = diagnostics.fieldReview;
	if (!fieldReview || typeof fieldReview !== 'object') return order;
	const nextDiagnostics = { ...diagnostics };
	const nextEditors = Array.isArray(fieldReview.editors)
		? fieldReview.editors.filter(
				editor => fieldIds && !fieldIds.has(String(editor?.fieldId)),
			)
		: [];
	if (fieldIds && nextEditors.length)
		nextDiagnostics.fieldReview = { ...fieldReview, editors: nextEditors };
	else delete nextDiagnostics.fieldReview;
	return { ...order, importDiagnostics: nextDiagnostics };
};

const clearSharedSignedReviewArtifacts = order => {
	const restoredZoneOrder = getZoneCreationSelectionsFromOrders([order]).reduce(
		(updatedOrder, selection) =>
			clearZoneCreationSelection({ order: updatedOrder, selection }),
		order,
	);
	const diagnostics = { ...getImportDiagnostics(restoredZoneOrder) };
	delete diagnostics.serviceReviewSession;
	delete diagnostics.fieldReview;
	delete diagnostics.serviceCreation;
	delete diagnostics.service_creation;
	delete diagnostics.serviceCatalog;
	delete diagnostics.service_catalog;
	['serviceMatch', 'service_match'].forEach(key => {
		if (!diagnostics[key] || typeof diagnostics[key] !== 'object') return;
		const match = { ...diagnostics[key], candidates: [] };
		[
			'allowManualSelection',
			'allow_manual_selection',
			'canCreate',
			'can_create',
			'challenge',
			'reviewChallenge',
			'selectionChallenge',
		].forEach(capability => delete match[capability]);
		diagnostics[key] = match;
	});
	if (diagnostics.assignment && typeof diagnostics.assignment === 'object') {
		diagnostics.assignment = {
			...diagnostics.assignment,
			candidates: [],
		};
		delete diagnostics.assignment.reviewChallenge;
	}
	if (Array.isArray(diagnostics.dateDefaults))
		diagnostics.dateDefaults = diagnostics.dateDefaults.map(item => {
			if (!item || typeof item !== 'object') return item;
			const nextItem = { ...item };
			delete nextItem.challenge;
			return nextItem;
		});
	const clearZoneCreation = match => {
		if (!match || typeof match !== 'object') return match;
		const nextMatch = { ...match };
		delete nextMatch.zoneCreation;
		return nextMatch;
	};
	if (
		diagnostics.locationMatches &&
		typeof diagnostics.locationMatches === 'object'
	)
		diagnostics.locationMatches = Object.keys(
			diagnostics.locationMatches,
		).reduce(
			(result, fieldId) => ({
				...result,
				[fieldId]: clearZoneCreation(diagnostics.locationMatches[fieldId]),
			}),
			{},
		);
	if (
		diagnostics.locationMatch &&
		typeof diagnostics.locationMatch === 'object'
	)
		diagnostics.locationMatch = clearZoneCreation(diagnostics.locationMatch);
	return invalidateContractFrequencyCreationCapabilities(
		invalidateContractFrequencyCapabilities({
			...restoredZoneOrder,
			importDiagnostics: diagnostics,
		}),
	);
};

const invalidateAssignmentCandidateCapabilities = (
	order,
	{
		preserveReviewChallenge = false,
		reasonCode = 'ASSIGNMENT_CONTEXT_REVALIDATION_REQUIRED',
		message = 'El servicio o la ubicación cambió. Valida los cambios antes de elegir técnico.',
	} = {},
) => {
	const diagnostics = getImportDiagnostics(order);
	const assignment = diagnostics.assignment;
	if (!assignment || typeof assignment !== 'object') return order;
	const invalidatedAssignment = {
		...assignment,
		// Candidate challenges and mutability are issued for the current
		// service/location context and must never survive a local context change.
		candidates: [],
		mutability: {
			allowed: false,
			reasonCode,
			message,
		},
	};
	if (!preserveReviewChallenge || !invalidatedAssignment.reviewChallenge) {
		delete invalidatedAssignment.reviewChallenge;
		delete invalidatedAssignment.approvalRequired;
	}
	delete invalidatedAssignment.selection;
	delete invalidatedAssignment.selections;
	return {
		...order,
		importDiagnostics: {
			...diagnostics,
			assignment: invalidatedAssignment,
		},
	};
};

const invalidateServiceCandidateCapabilities = (
	order,
	{ invalidateCreation = false } = {},
) => {
	const diagnostics = getImportDiagnostics(order);
	let changed = false;
	const invalidatedDiagnostics = { ...diagnostics };
	['serviceMatch', 'service_match'].forEach(key => {
		const match = diagnostics[key];
		if (!match || typeof match !== 'object') return;
		invalidatedDiagnostics[key] = { ...match, candidates: [] };
		changed = true;
	});
	if (invalidateCreation) {
		['serviceCreation', 'service_creation'].forEach(key => {
			const creation = diagnostics[key];
			if (!creation || typeof creation !== 'object') return;
			const invalidatedCreation = {
				...creation,
				allowed: false,
				status: 'stale',
			};
			delete invalidatedCreation.challenge;
			delete invalidatedCreation.selectionChallenge;
			invalidatedDiagnostics[key] = invalidatedCreation;
			changed = true;
		});
	}
	return changed
		? { ...order, importDiagnostics: invalidatedDiagnostics }
		: order;
};

const invalidateZoneEligibilityCapabilities = (order, plans) => {
	const rowId = String(getImportRowIdentity(order));
	const targetedPlans = (plans || []).filter(plan =>
		(plan?.targetRowIds || []).map(String).includes(rowId),
	);
	if (!targetedPlans.length) return order;
	const diagnostics = getImportDiagnostics(order);
	const invalidatesCreation = (creation, fieldId) =>
		creation &&
		targetedPlans.some(
			plan =>
				String(plan.fieldId) === String(fieldId || creation.fieldId) &&
				(!plan.sourceKey ||
					!creation.sourceKey ||
					String(plan.sourceKey) === String(creation.sourceKey)),
		);
	const invalidateMatch = (match, fieldId) => {
		if (!match || typeof match !== 'object') return match;
		const creation = match.zoneCreation;
		if (!invalidatesCreation(creation, fieldId)) return match;
		const invalidatedCreation = {
			...creation,
			eligible: false,
			status: 'stale',
		};
		delete invalidatedCreation.eligibilityChallenge;
		return { ...match, zoneCreation: invalidatedCreation };
	};
	let changed = false;
	const invalidatedDiagnostics = { ...diagnostics };
	if (
		diagnostics.locationMatches &&
		typeof diagnostics.locationMatches === 'object'
	) {
		invalidatedDiagnostics.locationMatches = Object.keys(
			diagnostics.locationMatches,
		).reduce((result, fieldId) => {
			const current = diagnostics.locationMatches[fieldId];
			const invalidated = invalidateMatch(current, fieldId);
			if (invalidated !== current) changed = true;
			result[fieldId] = invalidated;
			return result;
		}, {});
	}
	if (
		diagnostics.locationMatch &&
		typeof diagnostics.locationMatch === 'object'
	) {
		const fieldId = diagnostics.locationMatch.zoneCreation?.fieldId;
		const invalidated = invalidateMatch(diagnostics.locationMatch, fieldId);
		if (invalidated !== diagnostics.locationMatch) changed = true;
		invalidatedDiagnostics.locationMatch = invalidated;
	}
	return changed
		? { ...order, importDiagnostics: invalidatedDiagnostics }
		: order;
};

const ImportPreview = ({
	isMobile,
	orders,
	filters,
	dateFormat,
	templateValidator,
	onSetState,
	onResetOrders,
	onDeleteRow,
	sendToast,
	initialImportReviewError,
}) => {
	const { getCreationAssignment, create: createOrders } = useOrderApi();
	const { theme, isDarkTheme, themeProfile } = useTheme();
	const importReviewThemeStyle = getImportReviewThemeStyle(theme, isDarkTheme);
	const [isValidating, setIsValidating] = React.useState(false);
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [importReviewError, setImportReviewError] = React.useState(
		initialImportReviewError,
	);
	const [pendingConfirmation, setPendingConfirmation] = React.useState();
	const [zoneCreationSelections, setZoneCreationSelections] = React.useState(
		() =>
			getZoneCreationSelectionsFromOrders(orders).map(selection => ({
				...selection,
				validated: true,
			})),
	);
	const [serviceCreationSelections, setServiceCreationSelections] =
		React.useState([]);
	const [serviceReviewSelections, setServiceReviewSelections] = React.useState(
		[],
	);
	const [assignmentReviewSelections, setAssignmentReviewSelections] =
		React.useState([]);
	const [
		contractFrequencyReviewSelections,
		setContractFrequencyReviewSelections,
	] = React.useState([]);
	const [
		contractFrequencyCreationRequests,
		setContractFrequencyCreationRequests,
	] = React.useState([]);
	const [
		contractFrequencyCreationSelections,
		setContractFrequencyCreationSelections,
	] = React.useState([]);
	const [fieldReviewSelections, setFieldReviewSelections] = React.useState([]);
	const [fieldReviewSnapshot, setFieldReviewSnapshot] = React.useState([]);
	const [selectedBulkRowIds, setSelectedBulkRowIds] = React.useState([]);
	const [isBulkConfirmationPending, setIsBulkConfirmationPending] =
		React.useState(false);
	const busyRef = React.useRef(false);
	const isBusy = isValidating || isSubmitting;
	const isBackgroundBlocked = isBusy || isBulkConfirmationPending;

	const reviewOrders = React.useMemo(
		() => assignImportRowIdentities(orders),
		[orders],
	);
	const previewOrders = React.useMemo(
		() => filterDataByFilterOptions(reviewOrders, filters),
		[reviewOrders, filters],
	);
	const fields = React.useMemo(
		() => templateValidator?.dataStructureObject?.fields || [],
		[templateValidator],
	);
	const templateValidatorFields = React.useMemo(
		() => ImportUtils.getTemplateValidatorFields(templateValidator),
		[templateValidator],
	);

	const {
		cityIdField,
		townshipIdField,
		assignedTechIdField,
		serviceIdField,
		contractFrequencyIdField,
	} = templateValidatorFields;
	const zoneFields = React.useMemo(
		() =>
			getEditableZoneFields({
				templateValidatorFields,
				orders: reviewOrders,
			}),
		[templateValidatorFields, reviewOrders],
	);
	const locationFields = React.useMemo(
		() => ({ cityIdField, townshipIdField, zoneFields }),
		[cityIdField, townshipIdField, zoneFields],
	);
	const assignmentContextFieldIds = React.useMemo(
		() =>
			new Set(
				[
					serviceIdField?.id,
					cityIdField?.id,
					townshipIdField?.id,
					...zoneFields.map(field => field.id),
				]
					.filter(fieldId => fieldId !== undefined && fieldId !== null)
					.map(String),
			),
		[serviceIdField, cityIdField, townshipIdField, zoneFields],
	);
	const changesAssignmentContext = React.useCallback(
		fieldId =>
			fieldId !== undefined &&
			fieldId !== null &&
			assignmentContextFieldIds.has(String(fieldId)),
		[assignmentContextFieldIds],
	);
	const changesContractFrequencyContext = React.useCallback(
		fieldId =>
			fieldId !== undefined &&
			fieldId !== null &&
			((contractFrequencyIdField?.id !== undefined &&
				contractFrequencyIdField?.id !== null &&
				String(fieldId) === String(contractFrequencyIdField.id)) ||
				String(fieldId) === 'contractNumber'),
		[contractFrequencyIdField],
	);
	const getContractFrequencyCreationCohortInvalidation = changedRowIds =>
		invalidateContractFrequencyCreationCohorts({
			orders: reviewOrders,
			requests: contractFrequencyCreationRequests,
			selections: contractFrequencyCreationSelections,
			changedRowIds,
		});
	const clearContractFrequencyCreationCohorts = cohortKeys => {
		const affectedCohorts = new Set(cohortKeys || []);
		if (!affectedCohorts.size) return;
		setContractFrequencyCreationRequests(current =>
			current.filter(item => !affectedCohorts.has(item.cohortKey)),
		);
		setContractFrequencyCreationSelections(current =>
			current.filter(item => !affectedCohorts.has(item.cohortKey)),
		);
	};
	const hasEffectiveBlockingIssues = React.useCallback(
		(values, serviceSelections, frequencyCreationSelections) =>
			values.some(order =>
				getEffectiveOrderReviewState({
					order,
					fields,
					locationFields,
					contractFrequencyCreationSelections: frequencyCreationSelections,
				}).issues.some(issue => {
					if (issue?.blocking !== true) return false;
					const coveredBySignedServicePlan =
						isServiceIssueResolvedByCreation(issue) &&
						serviceSelections.some(selection =>
							isOrderTargetedByServiceCreation(order, selection),
						);
					return !coveredBySignedServicePlan;
				}),
			),
		[fields, locationFields],
	);
	const blockingCount = React.useMemo(
		() =>
			reviewOrders.filter(order =>
				hasEffectiveBlockingIssues(
					[order],
					serviceCreationSelections,
					contractFrequencyCreationSelections,
				),
			).length,
		[
			reviewOrders,
			serviceCreationSelections,
			contractFrequencyCreationSelections,
			hasEffectiveBlockingIssues,
		],
	);
	const reviewSummary = React.useMemo(
		() =>
			getReviewSummary(reviewOrders, fields, locationFields, {
				contractFrequencyCreationSelections,
			}),
		[reviewOrders, fields, locationFields, contractFrequencyCreationSelections],
	);
	const autoCorrectedCount = reviewSummary[REVIEW_STATUS.AUTO_CORRECTED];
	const zoneCoverageChangeCount = React.useMemo(
		() => getZoneCoverageChangeCount(reviewOrders),
		[reviewOrders],
	);
	const hasUnvalidatedChanges = React.useMemo(
		() =>
			Boolean(importReviewError) ||
			requiresRevalidation(reviewOrders) ||
			zoneCreationSelections.some(selection => selection.validated !== true) ||
			serviceCreationSelections.some(
				selection => selection.validated !== true,
			) ||
			assignmentReviewSelections.some(
				selection => selection.validated !== true,
			) ||
			contractFrequencyReviewSelections.some(
				selection => selection.validated !== true,
			) ||
			contractFrequencyCreationRequests.length > 0 ||
			contractFrequencyCreationSelections.some(
				selection => selection.validated !== true,
			) ||
			fieldReviewSelections.length > 0,
		[
			importReviewError,
			reviewOrders,
			zoneCreationSelections,
			serviceCreationSelections,
			assignmentReviewSelections,
			contractFrequencyReviewSelections,
			contractFrequencyCreationRequests,
			contractFrequencyCreationSelections,
			fieldReviewSelections,
		],
	);
	const zoneCreationSummary = React.useMemo(
		() => getZoneCreationSummary(reviewOrders, zoneCreationSelections),
		[reviewOrders, zoneCreationSelections],
	);
	const assignmentOptions = React.useMemo(
		() => ({
			dateFormat,
			validationTemplateId: templateValidator.id,
		}),
		[dateFormat, templateValidator.id],
	);
	const zoneCreationSelectionPayload = React.useMemo(
		() =>
			getZoneCreationSelectionsPayload(zoneCreationSelections, reviewOrders),
		[zoneCreationSelections, reviewOrders],
	);
	const serviceCreationSelectionPayload = React.useMemo(
		() => getServiceCreationSelectionsPayload(serviceCreationSelections),
		[serviceCreationSelections],
	);
	const serviceReviewSelectionPayload = React.useMemo(
		() => getServiceReviewSelectionsPayload(serviceReviewSelections),
		[serviceReviewSelections],
	);
	const assignmentReviewSelectionPayload = React.useMemo(
		() => getAssignmentReviewSelectionsPayload(assignmentReviewSelections),
		[assignmentReviewSelections],
	);
	const contractFrequencyReviewSelectionPayload = React.useMemo(
		() =>
			getContractFrequencyReviewSelectionsPayload(
				contractFrequencyReviewSelections,
			),
		[contractFrequencyReviewSelections],
	);
	const contractFrequencyCreationValidationRequestPayload = React.useMemo(
		() =>
			getContractFrequencyCreationRequestsPayload([
				...contractFrequencyCreationRequests,
				...contractFrequencyCreationSelections,
			]),
		[contractFrequencyCreationRequests, contractFrequencyCreationSelections],
	);
	const contractFrequencyCreationSummary = React.useMemo(
		() =>
			getContractFrequencyCreationSummary(contractFrequencyCreationSelections),
		[contractFrequencyCreationSelections],
	);
	const fieldReviewSelectionPayload = React.useMemo(
		() => getFieldReviewSelectionsPayload(fieldReviewSelections),
		[fieldReviewSelections],
	);
	const coverageFingerprint = React.useCallback(
		values => JSON.stringify(getZoneCoverageChangeKeys(values).slice().sort()),
		[],
	);
	const currentAssignmentFingerprint = React.useMemo(
		() => getAssignmentSnapshotFingerprint(reviewOrders, assignedTechIdField),
		[reviewOrders, assignedTechIdField],
	);
	const currentAssignmentProofFingerprint = React.useMemo(
		() =>
			getAssignmentReviewProofFingerprint(reviewOrders, assignedTechIdField),
		[reviewOrders, assignedTechIdField],
	);
	const currentCoverageFingerprint = React.useMemo(
		() => coverageFingerprint(reviewOrders),
		[coverageFingerprint, reviewOrders],
	);
	const currentZoneCreationFingerprint = React.useMemo(
		() => getZoneCreationSnapshotFingerprint(reviewOrders),
		[reviewOrders],
	);
	const isPendingSnapshotVisible =
		pendingConfirmation?.assignmentFingerprint ===
			currentAssignmentFingerprint &&
		pendingConfirmation?.assignmentProofFingerprint ===
			currentAssignmentProofFingerprint &&
		pendingConfirmation?.coverageFingerprint === currentCoverageFingerprint &&
		pendingConfirmation?.zoneCreationFingerprint ===
			currentZoneCreationFingerprint;
	const hasPersistentAssignmentChallenge = React.useMemo(
		() => hasAssignmentApprovalRequired(reviewOrders),
		[reviewOrders],
	);

	const getErrorValue = err => (Array.isArray(err) ? err[0] || {} : err || {});
	const hasErrorCode = (err, code) => {
		const envelope = err && typeof err === 'object' ? err : {};
		const errors = Array.isArray(err)
			? err
			: Array.isArray(envelope.errors)
				? envelope.errors
				: [];
		return (
			envelope.code === code ||
			envelope.name === code ||
			errors.some(error => error?.code === code || error?.name === code)
		);
	};
	const getReviewDomains = response =>
		Array.from(
			new Set(
				[
					response?.reviewDomain,
					response?.domain,
					...(Array.isArray(response?.reviewDomains)
						? response.reviewDomains
						: []),
				]
					.filter(domain => domain !== undefined && domain !== null)
					.map(domain => String(domain).trim().toLowerCase())
					.filter(Boolean),
			),
		);
	const hasReviewDomain = (response, expectedDomain) =>
		getReviewDomains(response).includes(
			String(expectedDomain || '')
				.trim()
				.toLowerCase(),
		);
	const resetSignedReviewOrder = (order, sourceOrder = order) => {
		const rowId = String(getImportRowIdentity(sourceOrder));
		const sourceDiagnostics = getImportDiagnostics(sourceOrder);
		const resetBaseOrder = hasOwn(sourceDiagnostics, 'sourceFieldValues')
			? {
					...order,
					importDiagnostics: {
						...getImportDiagnostics(order),
						sourceFieldValues: sourceDiagnostics.sourceFieldValues,
					},
				}
			: order;
		const fieldArtifacts = [
			...getFieldReviewDescriptors(sourceOrder),
			...fieldReviewSelections.filter(
				selection => String(selection.rowId) === rowId,
			),
		];
		const frequencySelection = contractFrequencyReviewSelections.find(
			selection => String(selection.rowId) === rowId,
		);
		const frequencyRestoredOrder = restoreContractFrequencyState(
			resetBaseOrder,
			frequencySelection?.previousState,
		);
		const serviceRestoredOrder =
			serviceIdField &&
			Object.keys(getServicePresentation(sourceOrder).previous).length
				? undoManualServiceSelection({
						order: frequencyRestoredOrder,
						serviceIdField,
					})
				: frequencyRestoredOrder;
		return markOrderForRevalidation(
			invalidateAssignmentCandidateCapabilities(
				clearSharedSignedReviewArtifacts(
					restoreSignedSourceFieldReviewValues(
						serviceRestoredOrder,
						fieldArtifacts,
					),
				),
			),
		);
	};
	const clearSignedReviewState = () => {
		setZoneCreationSelections([]);
		setServiceReviewSelections([]);
		setServiceCreationSelections([]);
		setAssignmentReviewSelections([]);
		setContractFrequencyReviewSelections([]);
		setContractFrequencyCreationRequests([]);
		setContractFrequencyCreationSelections([]);
		setFieldReviewSelections([]);
		setFieldReviewSnapshot([]);
		setPendingConfirmation(undefined);
	};
	const showImportError = (err, fallbackMessage) => {
		const error = getErrorValue(err);
		sendToast({ message: error.message || fallbackMessage, type: 'error' });
		if (Array.isArray(error.orders)) {
			onSetState({
				orders: mergeImportDiagnostics(error.orders, reviewOrders),
			});
		}
	};
	const handlePartialCreateResult = response => {
		const outcome = getImportCreationOutcome(response, reviewOrders);
		if (!outcome.isPartial) return false;
		const reviewedRowIds = new Set(
			reviewOrders.map(order => String(getImportRowIdentity(order))),
		);
		const succeeded = new Set(
			outcome.succeededRowIds.filter(rowId =>
				reviewedRowIds.has(String(rowId)),
			),
		);
		const mergedResults = mergeImportDiagnostics(
			outcome.resultOrders,
			reviewOrders,
		);
		const resultByRowId = new Map(
			mergedResults.map(order => [String(getImportRowIdentity(order)), order]),
		);
		const zoneOutcomeByRowId = new Map(
			(outcome.outcomes || [])
				.filter(item => item?.rowId !== undefined)
				.map(item => [String(item.rowId), item]),
		);
		const remainingOrders = reviewOrders
			.filter(order => !succeeded.has(String(getImportRowIdentity(order))))
			.map(order => {
				const rowId = String(getImportRowIdentity(order));
				const resultOrder = resultByRowId.get(rowId) || order;
				const zoneOutcome = zoneOutcomeByRowId.get(rowId);
				const orderWithOutcome = {
					...resultOrder,
					...(zoneOutcome?.zoneCreationResult
						? { zoneCreationResult: zoneOutcome.zoneCreationResult }
						: {}),
					...(Array.isArray(zoneOutcome?.zoneCreationResults)
						? { zoneCreationResults: zoneOutcome.zoneCreationResults }
						: {}),
				};
				return resetSignedReviewOrder(orderWithOutcome, order);
			});
		onSetState({ orders: remainingOrders });
		const remainingZoneSelections = getZoneCreationSelectionsFromOrders(
			remainingOrders,
		).map(selection => ({
			...selection,
			validated: false,
		}));
		setZoneCreationSelections(remainingZoneSelections);
		// A partial create can change the scoped service catalog. Never replay
		// candidate or archetype challenges against the remaining rows.
		setServiceReviewSelections([]);
		setServiceCreationSelections([]);
		setAssignmentReviewSelections([]);
		setContractFrequencyReviewSelections([]);
		setContractFrequencyCreationRequests([]);
		setContractFrequencyCreationSelections([]);
		// Successful rows leave the batch and failed/stale proofs are not reusable.
		// Keeping either set would replay a capability against the wrong retry batch.
		setFieldReviewSelections([]);
		setFieldReviewSnapshot([]);
		setPendingConfirmation(undefined);
		if (
			hasAssignmentApprovalRequired(remainingOrders) ||
			remainingZoneSelections.length > 0
		) {
			setPendingConfirmation({
				assignmentFingerprint: getAssignmentSnapshotFingerprint(
					remainingOrders,
					assignedTechIdField,
				),
				assignmentProofFingerprint: getAssignmentReviewProofFingerprint(
					remainingOrders,
					assignedTechIdField,
				),
				coverageFingerprint: coverageFingerprint(remainingOrders),
				zoneCreationFingerprint:
					getZoneCreationSnapshotFingerprint(remainingOrders),
				assignmentChanged: false,
				assignmentReviewRequired:
					hasAssignmentApprovalRequired(remainingOrders),
				coverageChanged: false,
				zoneCreationChanged: remainingZoneSelections.length > 0,
			});
		}
		const successCount = succeeded.size;
		const failedCount = remainingOrders.length;
		sendToast({
			message: `${successCount} ${
				successCount === 1 ? 'orden importada' : 'órdenes importadas'
			}; ${failedCount} ${
				failedCount === 1 ? 'requiere' : 'requieren'
			} revalidación antes de continuar.`,
			type: 'warn',
		});
		return true;
	};
	const handleMultiDomainStaleReviewResult = response => {
		if (
			!hasErrorCode(response, 'IMPORT_REVIEW_STALE') ||
			getReviewDomains(response).length < 2
		)
			return false;
		onSetState({
			orders: reviewOrders.map(order => resetSignedReviewOrder(order)),
		});
		clearSignedReviewState();
		sendToast({
			message:
				response?.message ||
				'La revisión cambió en varios campos. Se descartaron las autorizaciones anteriores; valida nuevamente antes de importar.',
			type: 'warn',
		});
		return true;
	};
	const handleStaleContractFrequencyResult = response => {
		if (!hasErrorCode(response, 'IMPORT_REVIEW_STALE')) return false;
		const reason = String(response?.reason || '').toLowerCase();
		const isFrequencyStale =
			hasReviewDomain(response, 'frequency') ||
			Boolean(response?.frequencyReviewFailure) ||
			reason.startsWith('contract_frequency_selection_') ||
			reason.startsWith('contract_frequency_creation_') ||
			reason === 'contract_frequency_contract_conflict';
		if (!isFrequencyStale) return false;

		const recoveredOrders = reviewOrders.map(order =>
			markOrderForRevalidation(
				invalidateContractFrequencyCreationCapabilities(
					invalidateContractFrequencyCapabilities(order),
				),
			),
		);
		onSetState({ orders: recoveredOrders });
		setContractFrequencyReviewSelections([]);
		setContractFrequencyCreationRequests([]);
		setContractFrequencyCreationSelections([]);
		setPendingConfirmation(undefined);
		sendToast({
			message:
				response?.message ||
				'La autorización de frecuencia venció o cambió. Valida nuevamente y selecciona una frecuencia disponible.',
			type: 'warn',
		});
		return true;
	};
	const handleStaleZoneCreationResult = response => {
		const reviewDomain = response?.reviewDomain || response?.domain;
		if (reviewDomain && String(reviewDomain).toLowerCase() !== 'zone')
			return false;
		if (
			!hasErrorCode(response, 'IMPORT_REVIEW_STALE') ||
			zoneCreationSelections.length === 0
		)
			return false;
		const failure = response?.zoneCreationFailure || {};
		const failedTargetRowIds = new Set(
			(Array.isArray(failure.targetRowIds) ? failure.targetRowIds : [])
				.filter(rowId => rowId !== undefined && rowId !== null)
				.map(String),
		);
		const hasTrustedSelector =
			failure.fieldId != null ||
			failure.sourceKey != null ||
			failedTargetRowIds.size > 0;
		const matchedPlans = hasTrustedSelector
			? zoneCreationSelections.filter(selection => {
					if (
						failure.fieldId != null &&
						String(selection.fieldId) !== String(failure.fieldId)
					)
						return false;
					if (
						failure.sourceKey != null &&
						String(selection.sourceKey) !== String(failure.sourceKey)
					)
						return false;
					if (
						failure.fieldId == null &&
						failure.sourceKey == null &&
						failedTargetRowIds.size > 0
					)
						return (selection.targetRowIds || []).some(rowId =>
							failedTargetRowIds.has(String(rowId)),
						);
					return true;
				})
			: zoneCreationSelections;
		// A stale challenge must never survive recovery. If the server cannot map
		// trusted selectors to the current UI plan, fail closed and discard all.
		const invalidatedPlans = matchedPlans.length
			? matchedPlans
			: zoneCreationSelections;
		const responseOrders = Array.isArray(response?.orders)
			? mergeImportDiagnostics(response.orders, reviewOrders)
			: reviewOrders;
		const restoredOrders = clearZoneCreationPlans(
			invalidatedPlans,
			responseOrders,
		);
		const invalidatedRowIds = new Set(failedTargetRowIds);
		invalidatedPlans.forEach(selection =>
			(selection.targetRowIds || []).forEach(rowId =>
				invalidatedRowIds.add(String(rowId)),
			),
		);
		const recoveredOrders = restoredOrders.map(order =>
			invalidatedRowIds.size === 0 ||
			invalidatedRowIds.has(String(getImportRowIdentity(order)))
				? markOrderForRevalidation(
						invalidateZoneEligibilityCapabilities(order, invalidatedPlans),
					)
				: order,
		);
		onSetState({ orders: recoveredOrders });
		setZoneCreationSelections(current =>
			current.filter(
				selection =>
					!invalidatedPlans.some(
						invalidated => invalidated.challenge === selection.challenge,
					),
			),
		);
		setPendingConfirmation(undefined);
		const staleMessage =
			String(response?.reason || '').toLowerCase() === 'existing_order'
				? 'La orden ya existe. No se crearán ubicaciones ni se cambiará su zona actual.'
				: 'La revisión de la nueva zona venció. Vuelve a validar y selecciónala nuevamente.';
		sendToast({
			message: response?.message || staleMessage,
			type: 'warn',
		});
		return true;
	};
	const handleStaleServiceReviewResult = response => {
		if (!hasErrorCode(response, 'IMPORT_REVIEW_STALE')) return false;
		const reason = String(response?.reason || '').toLowerCase();
		const isServiceStale =
			reason.startsWith('service_') ||
			response?.serviceReviewFailure ||
			response?.serviceCreationFailure;
		if (!isServiceStale) return false;
		const authoritativeOrders = Array.isArray(response?.orders)
			? mergeImportDiagnostics(response.orders, reviewOrders)
			: reviewOrders.map(markOrderForRevalidation);
		const requiresSharedSessionReset = [
			'service_review_session_drift',
			'service_review_session_mixed',
			'service_review_preview_required',
		].includes(reason);
		const resetOrders = requiresSharedSessionReset
			? authoritativeOrders.map(order => {
					const rowId = String(getImportRowIdentity(order));
					const fieldArtifacts = [
						...getFieldReviewDescriptors(order),
						...fieldReviewSelections.filter(
							selection => String(selection.rowId) === rowId,
						),
					];
					const serviceRestoredOrder =
						serviceIdField &&
						Object.keys(getServicePresentation(order).previous).length
							? undoManualServiceSelection({ order, serviceIdField })
							: order;
					return markOrderForRevalidation(
						clearSharedSignedReviewArtifacts(
							restoreSignedSourceFieldReviewValues(
								serviceRestoredOrder,
								fieldArtifacts,
							),
						),
					);
				})
			: authoritativeOrders;
		const invalidatedRowIds = new Set([
			...serviceReviewSelections.map(selection => String(selection.rowId)),
			...serviceCreationSelections.flatMap(selection =>
				(selection.targetRowIds || []).map(String),
			),
		]);
		const invalidateEveryOrder = invalidatedRowIds.size === 0;
		const invalidateCreation =
			Boolean(response?.serviceCreationFailure) ||
			reason.startsWith('service_creation_');
		const recoveredOrders = resetOrders.map(order =>
			invalidateEveryOrder ||
			invalidatedRowIds.has(String(getImportRowIdentity(order)))
				? markOrderForRevalidation(
						invalidateServiceCandidateCapabilities(order, {
							invalidateCreation,
						}),
					)
				: order,
		);
		onSetState({ orders: recoveredOrders });
		setServiceReviewSelections([]);
		setServiceCreationSelections([]);
		if (requiresSharedSessionReset) {
			setFieldReviewSelections([]);
			setFieldReviewSnapshot([]);
			setAssignmentReviewSelections([]);
			setContractFrequencyReviewSelections([]);
		}
		setPendingConfirmation(undefined);
		sendToast({
			message:
				response?.message ||
				'La revisión del servicio venció o cambió. Vuelve a validar y selecciona el servicio nuevamente.',
			type: 'warn',
		});
		return true;
	};
	const handleStaleAssignmentSelectionResult = response => {
		if (!hasErrorCode(response, 'IMPORT_REVIEW_STALE')) return false;
		const reason = String(response?.reason || '').toLowerCase();
		const isAssignmentStale =
			response?.domain === 'assignment' ||
			reason.startsWith('assignment_') ||
			response?.assignmentReviewFailure;
		if (!isAssignmentStale) return false;
		const invalidatedRowIds = new Set(
			assignmentReviewSelections.map(selection => String(selection.rowId)),
		);
		const invalidateEveryOrder = invalidatedRowIds.size === 0;
		const preserveSemanticReviewChallenge =
			reason.startsWith('assignment_selection_') &&
			![
				'review_stale',
				'not_mutable',
				'candidate_scope',
				'context',
				'baseline',
				'expired',
			].some(staleReason => reason.endsWith(staleReason));
		const authoritativeOrders = Array.isArray(response?.orders)
			? mergeImportDiagnostics(response.orders, reviewOrders)
			: reviewOrders;
		const recoveredOrders = authoritativeOrders.map(order =>
			invalidateEveryOrder ||
			invalidatedRowIds.has(String(getImportRowIdentity(order)))
				? markOrderForRevalidation(
						invalidateAssignmentCandidateCapabilities(order, {
							preserveReviewChallenge: preserveSemanticReviewChallenge,
							reasonCode: 'ASSIGNMENT_REVIEW_STALE',
							message:
								'La autorización del técnico venció. Valida los cambios antes de elegirlo nuevamente.',
						}),
					)
				: order,
		);
		onSetState({ orders: recoveredOrders });
		// Discard the rejected per-candidate token. Empty candidates and blocked
		// mutability prevent the same token from being selected again before refresh.
		setAssignmentReviewSelections([]);
		setPendingConfirmation(undefined);
		sendToast({
			message:
				response?.message ||
				'La selección del técnico venció o cambió. Valida nuevamente y selecciona un técnico elegible.',
			type: 'warn',
		});
		return true;
	};
	const handleStaleFieldReviewResult = response => {
		if (!hasErrorCode(response, 'IMPORT_REVIEW_STALE')) return false;
		const reason = String(response?.reason || '').toLowerCase();
		const isFieldReviewStale =
			response?.reviewDomain === 'field' ||
			response?.domain === 'field' ||
			Boolean(response?.fieldReviewFailure) ||
			reason.startsWith('field_');
		if (!isFieldReviewStale) return false;
		const failure = response?.fieldReviewFailure || {};
		const failureRowIds = new Set(
			[
				...(Array.isArray(failure.targetRowIds) ? failure.targetRowIds : []),
				failure.rowId,
			]
				.filter(rowId => rowId !== undefined && rowId !== null)
				.map(String)
				.filter(Boolean),
		);
		const failureFieldId =
			failure.fieldId === undefined || failure.fieldId === null
				? undefined
				: String(failure.fieldId);
		const isTargetedArtifact = artifact =>
			(failureRowIds.size === 0 ||
				failureRowIds.has(String(artifact?.rowId))) &&
			(!failureFieldId || String(artifact?.fieldId) === failureFieldId);
		const invalidatedSelections =
			fieldReviewSelections.filter(isTargetedArtifact);
		const authoritativeOrders = Array.isArray(response?.orders)
			? mergeImportDiagnostics(response.orders, reviewOrders)
			: reviewOrders;
		onSetState({
			orders: authoritativeOrders.map(order => {
				const rowId = String(getImportRowIdentity(order));
				if (failureRowIds.size > 0 && !failureRowIds.has(rowId)) return order;
				const fieldIds = failureFieldId ? new Set([failureFieldId]) : undefined;
				const fieldArtifacts = [
					...getFieldReviewDescriptors(order).filter(
						editor => !failureFieldId || editor.fieldId === failureFieldId,
					),
					...invalidatedSelections.filter(
						selection => String(selection.rowId) === rowId,
					),
				];
				return markOrderForRevalidation(
					clearFieldReviewDescriptors(
						restoreStagedFieldReviewValues(order, fieldArtifacts),
						fieldIds,
					),
				);
			}),
		});
		setFieldReviewSelections(current =>
			current.filter(selection => !isTargetedArtifact(selection)),
		);
		setFieldReviewSnapshot(current =>
			current.filter(proof => !isTargetedArtifact(proof)),
		);
		setPendingConfirmation(undefined);
		sendToast({
			message:
				response?.message ||
				'La autorización del campo editado venció o cambió. Valida nuevamente antes de editarlo.',
			type: 'warn',
		});
		return true;
	};

	const onValidateChanges = async () => {
		if (busyRef.current || isBackgroundBlocked) return;
		busyRef.current = true;
		setIsValidating(true);
		const submittedContractFrequencyChallenges = new Set(
			contractFrequencyReviewSelectionPayload.map(
				selection => selection.challenge,
			),
		);
		try {
			const assignmentReviewSnapshot = getAssignmentReviewSnapshot(
				reviewOrders,
				assignedTechIdField,
			);
			const assignmentResponse = await getCreationAssignment(
				prepareImportOrdersForAssignment(reviewOrders),
				{
					...assignmentOptions,
					assignmentReviewSnapshot,
					...(zoneCreationSelectionPayload.length
						? { zoneCreationSelections: zoneCreationSelectionPayload }
						: {}),
					...(serviceCreationSelectionPayload.length
						? { serviceCreationSelections: serviceCreationSelectionPayload }
						: {}),
					...(serviceReviewSelectionPayload.length
						? { serviceReviewSelections: serviceReviewSelectionPayload }
						: {}),
					...(assignmentReviewSelectionPayload.length
						? {
								assignmentReviewSelections: assignmentReviewSelectionPayload,
							}
						: {}),
					...(fieldReviewSelectionPayload.length
						? { fieldReviewSelections: fieldReviewSelectionPayload }
						: {}),
					...(contractFrequencyReviewSelectionPayload.length
						? {
								contractFrequencyReviewSelections:
									contractFrequencyReviewSelectionPayload,
							}
						: {}),
					...(contractFrequencyCreationValidationRequestPayload.length
						? {
								contractFrequencyCreationRequests:
									contractFrequencyCreationValidationRequestPayload,
							}
						: {}),
					...(fieldReviewSnapshot.length ? { fieldReviewSnapshot } : {}),
				},
			);
			if (
				(assignmentReviewSelectionPayload.length ||
					contractFrequencyReviewSelectionPayload.length ||
					contractFrequencyCreationValidationRequestPayload.length) &&
				!hasSafeImportRowCorrelation(assignmentResponse, reviewOrders, {
					requireExact: true,
				})
			) {
				sendToast({
					message: contractFrequencyReviewSelectionPayload.length
						? 'La validación devolvió filas distintas a las revisadas. No se aplicó la selección de frecuencia.'
						: 'La validación devolvió filas distintas a las revisadas. No se aplicó la selección del técnico.',
					type: 'error',
				});
				return;
			}
			const validatedFieldReviewSnapshot =
				getFieldReviewSnapshot(assignmentResponse);
			if (
				fieldReviewSelections.length &&
				!hasSafeFieldReviewSnapshot(
					validatedFieldReviewSnapshot,
					fieldReviewSelections,
				)
			) {
				sendToast({
					message:
						'La validación no devolvió una prueba válida para el campo editado. No se aplicó el cambio; vuelve a intentarlo.',
					type: 'error',
				});
				return;
			}
			const validatedOrders = mergeImportDiagnostics(
				assignmentResponse,
				reviewOrders,
				{ markValidated: true },
			);
			setImportReviewError(undefined);
			onSetState({
				orders: validatedOrders,
			});
			setZoneCreationSelections(
				getZoneCreationSelectionsFromOrders(validatedOrders).map(selection => ({
					...selection,
					validated: true,
				})),
			);
			const refreshedServicePlans =
				getServiceCreationSelectionsFromOrders(validatedOrders);
			setServiceCreationSelections(current =>
				refreshedServicePlans
					.filter(plan =>
						current.some(
							selection =>
								selection.challenge === plan.challenge ||
								(selection.cohortKey && selection.cohortKey === plan.cohortKey),
						),
					)
					.map(plan => ({ ...plan, validated: true })),
			);
			setAssignmentReviewSelections([]);
			setContractFrequencyReviewSelections(current =>
				current.map(selection =>
					submittedContractFrequencyChallenges.has(selection.challenge)
						? { ...selection, validated: true }
						: selection,
				),
			);
			setContractFrequencyCreationRequests([]);
			const refreshedFrequencyCreationPlans =
				getContractFrequencyCreationPlansFromOrders(validatedOrders);
			setContractFrequencyCreationSelections(
				refreshedFrequencyCreationPlans
					.filter(plan =>
						contractFrequencyCreationSelections.some(selection =>
							areContractFrequencyCreationArtifactsSemanticallyEqual(
								selection,
								plan,
							),
						),
					)
					.map(plan => ({ ...plan, validated: true })),
			);
			if (fieldReviewSelections.length) {
				setFieldReviewSnapshot(validatedFieldReviewSnapshot);
				setFieldReviewSelections([]);
			}
			setPendingConfirmation(undefined);
			sendToast({
				message: 'Cambios validados. Revisa el resultado antes de importar.',
				type: 'success',
			});
		} catch (err) {
			if (handleMultiDomainStaleReviewResult(err)) return;
			if (handleStaleContractFrequencyResult(err)) return;
			if (handleStaleFieldReviewResult(err)) return;
			if (handleStaleAssignmentSelectionResult(err)) return;
			if (handleStaleServiceReviewResult(err)) return;
			if (handleStaleZoneCreationResult(err)) return;
			showImportError(
				err,
				'No fue posible validar los cambios. Revisa los datos e inténtalo nuevamente.',
			);
		} finally {
			busyRef.current = false;
			setIsValidating(false);
		}
	};

	const onCreateOrders = async () => {
		if (busyRef.current || isBackgroundBlocked) return;
		busyRef.current = true;
		setIsSubmitting(true);

		try {
			const reviewedAssignmentSnapshot = getAssignmentReviewSnapshot(
				reviewOrders,
				assignedTechIdField,
			);
			const reviewedAssignmentFingerprint = getAssignmentSnapshotFingerprint(
				reviewOrders,
				assignedTechIdField,
			);
			const reviewedAssignmentProofFingerprint =
				getAssignmentReviewProofFingerprint(reviewOrders, assignedTechIdField);
			const reviewedCoverageFingerprint = coverageFingerprint(reviewOrders);
			const reviewedZoneCreationFingerprint =
				getZoneCreationSnapshotFingerprint(reviewOrders);
			const reviewedZoneCreationSnapshot =
				getZoneCreationReviewSnapshot(reviewOrders);
			const reviewedZoneCreationSelections = getZoneCreationSelectionsPayload(
				zoneCreationSelections,
				reviewOrders,
			);
			const reviewedServiceCreationSelections =
				getServiceCreationSelectionsPayload(serviceCreationSelections);
			const reviewedServiceReviewSelections = getServiceReviewSelectionsPayload(
				serviceReviewSelections,
			);
			const reviewedContractFrequencyReviewSelections =
				getContractFrequencyReviewSelectionsPayload(
					contractFrequencyReviewSelections,
				);
			const reviewedContractFrequencyCreationRequests =
				getContractFrequencyCreationRequestsPayload(
					contractFrequencyCreationSelections,
				);
			const reviewedContractFrequencyCreationFingerprint =
				getContractFrequencyCreationPlanFingerprint(
					contractFrequencyCreationSelections,
				);
			const reviewedContractFrequencyFingerprint =
				getContractFrequencyReviewFingerprint(
					reviewOrders,
					contractFrequencyIdField,
				);
			const reviewedApprovalRequired =
				hasAssignmentApprovalRequired(reviewOrders);
			const approvalMatchesReviewedSnapshot =
				pendingConfirmation?.assignmentFingerprint ===
					reviewedAssignmentFingerprint &&
				pendingConfirmation?.assignmentProofFingerprint ===
					reviewedAssignmentProofFingerprint &&
				pendingConfirmation?.coverageFingerprint ===
					reviewedCoverageFingerprint &&
				pendingConfirmation?.zoneCreationFingerprint ===
					reviewedZoneCreationFingerprint;
			const assignmentResponse = await getCreationAssignment(
				prepareImportOrdersForAssignment(reviewOrders),
				{
					...assignmentOptions,
					assignmentReviewSnapshot: reviewedAssignmentSnapshot,
					...(reviewedZoneCreationSelections.length
						? { zoneCreationSelections: reviewedZoneCreationSelections }
						: {}),
					...(reviewedServiceCreationSelections.length
						? { serviceCreationSelections: reviewedServiceCreationSelections }
						: {}),
					...(reviewedServiceReviewSelections.length
						? { serviceReviewSelections: reviewedServiceReviewSelections }
						: {}),
					...(reviewedContractFrequencyReviewSelections.length
						? {
								contractFrequencyReviewSelections:
									reviewedContractFrequencyReviewSelections,
							}
						: {}),
					...(reviewedContractFrequencyCreationRequests.length
						? {
								contractFrequencyCreationRequests:
									reviewedContractFrequencyCreationRequests,
							}
						: {}),
					...(fieldReviewSnapshot.length ? { fieldReviewSnapshot } : {}),
				},
			);
			if (
				!hasSafeImportRowCorrelation(assignmentResponse, reviewOrders, {
					requireExact: true,
				})
			) {
				sendToast({
					message:
						'La validación devolvió filas distintas a las revisadas. No se importó nada; vuelve a validar el archivo.',
					type: 'error',
				});
				return;
			}
			const refreshedFieldReviewSnapshot =
				getFieldReviewSnapshot(assignmentResponse);
			if (
				fieldReviewSnapshot.length &&
				!hasSafeFieldReviewSnapshot(
					refreshedFieldReviewSnapshot,
					fieldReviewSnapshot,
					{ requireExact: true },
				)
			) {
				sendToast({
					message:
						'La autorización para el campo editado venció o cambió. Valida los cambios nuevamente antes de importar.',
					type: 'error',
				});
				return;
			}
			const assignedOrders = mergeImportDiagnostics(
				assignmentResponse,
				reviewOrders,
			);
			const refreshedContractFrequencyFingerprint =
				getContractFrequencyReviewFingerprint(
					assignedOrders,
					contractFrequencyIdField,
				);
			const hasUnreviewedContractFrequencyChanges =
				Boolean(contractFrequencyIdField) &&
				refreshedContractFrequencyFingerprint !==
					reviewedContractFrequencyFingerprint;
			const refreshedCoverageChangeCount =
				getZoneCoverageChangeCount(assignedOrders);
			const refreshedCoverageFingerprint = coverageFingerprint(assignedOrders);
			const refreshedZoneCreationFingerprint =
				getZoneCreationSnapshotFingerprint(assignedOrders);
			const refreshedZoneCreationSnapshot =
				getZoneCreationReviewSnapshot(assignedOrders);
			const refreshedZoneCreationSelections =
				getZoneCreationSelectionsFromOrders(assignedOrders).map(selection => ({
					...selection,
					validated: true,
				}));
			const refreshedServicePlans =
				getServiceCreationSelectionsFromOrders(assignedOrders);
			const refreshedServiceCreationSelections = refreshedServicePlans
				.filter(plan =>
					serviceCreationSelections.some(
						selection =>
							selection.challenge === plan.challenge ||
							(selection.cohortKey && selection.cohortKey === plan.cohortKey),
					),
				)
				.map(plan => ({ ...plan, validated: true }));
			const refreshedContractFrequencyCreationSelections =
				getContractFrequencyCreationPlansFromOrders(assignedOrders)
					.filter(plan =>
						contractFrequencyCreationSelections.some(
							selection =>
								selection.challenge === plan.challenge ||
								selection.cohortKey === plan.cohortKey,
						),
					)
					.map(plan => ({ ...plan, validated: true }));
			const hasUnreviewedContractFrequencyCreationChanges =
				getContractFrequencyCreationPlanFingerprint(
					refreshedContractFrequencyCreationSelections,
				) !== reviewedContractFrequencyCreationFingerprint;
			const hasUnreviewedServiceCreationChanges =
				getServiceCreationPlanFingerprint(
					refreshedServiceCreationSelections,
				) !== getServiceCreationPlanFingerprint(serviceCreationSelections);
			const hasUnreviewedCoverageChanges =
				refreshedCoverageFingerprint !== reviewedCoverageFingerprint;
			const assignmentDriftRowIds = getAssignmentDriftRowIds(
				reviewOrders,
				assignedOrders,
				assignedTechIdField,
			);
			const hasUnreviewedAssignmentChanges = assignmentDriftRowIds.length > 0;
			const refreshedAssignmentProofFingerprint =
				getAssignmentReviewProofFingerprint(
					assignedOrders,
					assignedTechIdField,
				);
			const hasUnreviewedAssignmentProof =
				refreshedAssignmentProofFingerprint !==
				reviewedAssignmentProofFingerprint;
			const hasUnreviewedZoneCreationChanges =
				refreshedZoneCreationFingerprint !== reviewedZoneCreationFingerprint ||
				(refreshedZoneCreationSnapshot.length > 0 &&
					reviewedZoneCreationSnapshot.length === 0);
			const refreshedApprovalRequired =
				hasAssignmentApprovalRequired(assignedOrders);
			const hasNewAssignmentChallenge =
				refreshedApprovalRequired && !reviewedApprovalRequired;

			if (
				hasEffectiveBlockingIssues(
					assignedOrders,
					refreshedServiceCreationSelections,
					contractFrequencyCreationSelections,
				)
			) {
				onSetState({ orders: assignedOrders });
				sendToast({
					message:
						'La validación encontró incidencias bloqueantes. Corrígelas antes de importar.',
					type: 'error',
				});
				return;
			}

			if (
				hasUnreviewedCoverageChanges ||
				hasUnreviewedAssignmentChanges ||
				hasUnreviewedAssignmentProof ||
				hasNewAssignmentChallenge ||
				hasUnreviewedZoneCreationChanges ||
				hasUnreviewedServiceCreationChanges ||
				hasUnreviewedContractFrequencyCreationChanges ||
				hasUnreviewedContractFrequencyChanges
			) {
				const assignmentReviewableOrders = hasUnreviewedAssignmentChanges
					? markAssignmentDrift(
							assignedOrders,
							reviewOrders,
							assignedTechIdField,
						)
					: assignedOrders;
				const reviewableOrders = hasUnreviewedContractFrequencyChanges
					? assignmentReviewableOrders.map(markOrderForRevalidation)
					: assignmentReviewableOrders;
				onSetState({ orders: reviewableOrders });
				setZoneCreationSelections(refreshedZoneCreationSelections);
				setServiceCreationSelections(refreshedServiceCreationSelections);
				setContractFrequencyCreationSelections(
					refreshedContractFrequencyCreationSelections.map(selection => ({
						...selection,
						validated: false,
					})),
				);
				if (hasUnreviewedContractFrequencyChanges)
					setContractFrequencyReviewSelections(current =>
						current.map(selection => ({ ...selection, validated: false })),
					);
				setPendingConfirmation({
					assignmentFingerprint: getAssignmentSnapshotFingerprint(
						reviewableOrders,
						assignedTechIdField,
					),
					assignmentProofFingerprint: getAssignmentReviewProofFingerprint(
						reviewableOrders,
						assignedTechIdField,
					),
					coverageFingerprint: coverageFingerprint(reviewableOrders),
					zoneCreationFingerprint:
						getZoneCreationSnapshotFingerprint(reviewableOrders),
					assignmentChanged: hasUnreviewedAssignmentChanges,
					assignmentReviewRequired:
						hasUnreviewedAssignmentChanges ||
						hasUnreviewedAssignmentProof ||
						hasNewAssignmentChallenge,
					coverageChanged: hasUnreviewedCoverageChanges,
					zoneCreationChanged: hasUnreviewedZoneCreationChanges,
				});
				sendToast({
					message: hasUnreviewedContractFrequencyCreationChanges
						? 'El plan de creación de frecuencias cambió durante la validación. Revisa su cadencia, alcance y confirma nuevamente.'
						: hasUnreviewedContractFrequencyChanges
							? 'La frecuencia contractual cambió durante la validación final. Revisa el valor actualizado y valida nuevamente antes de importar.'
							: hasUnreviewedServiceCreationChanges
								? 'El plan de creación de servicios cambió durante la validación. Revisa el arquetipo, el impacto y confirma nuevamente.'
								: hasUnreviewedZoneCreationChanges
									? 'El plan de zonas, asignación o cobertura cambió durante la validación. Revisa el resultado consolidado y confirma nuevamente.'
									: hasUnreviewedAssignmentChanges &&
										  hasUnreviewedCoverageChanges
										? 'La asignación y la cobertura cambiaron durante la validación. Revisa el nuevo resultado y confirma nuevamente.'
										: hasUnreviewedAssignmentChanges
											? 'La asignación cambió durante la validación. Revisa el técnico anterior y el nuevo, y confirma nuevamente.'
											: hasUnreviewedAssignmentProof ||
												  hasNewAssignmentChallenge
												? 'La revisión de asignación venció o dejó de ser válida; revisa y confirma nuevamente.'
												: 'La validación detectó cambios provisionales de cobertura. Revisa el resumen y confirma nuevamente la importación.',
					type: 'warn',
				});
				return;
			}

			const assignmentReviewSnapshot = getAssignmentReviewSnapshot(
				assignedOrders,
				assignedTechIdField,
			);
			const finalZoneCreationSelections = getZoneCreationSelectionsPayload(
				refreshedZoneCreationSelections,
				assignedOrders,
			);
			const finalServiceCreationSelections =
				getServiceCreationSelectionsPayload(refreshedServiceCreationSelections);
			const finalContractFrequencyCreationSelections =
				getContractFrequencyCreationSelectionsPayload(
					refreshedContractFrequencyCreationSelections,
				);
			const hasSignedDateDefaults = assignedOrders.some(
				order =>
					Array.isArray(order?.importDiagnostics?.dateDefaults) &&
					order.importDiagnostics.dateDefaults.length > 0,
			);
			const hasSealedServiceReviewSession = assignedOrders.some(
				order =>
					order?.importDiagnostics?.serviceReviewSession &&
					Object.keys(order.importDiagnostics.serviceReviewSession).length > 0,
			);
			const hasContractFrequencyReviewChallenge = assignedOrders.some(order => {
				const diagnostics = getImportDiagnostics(order);
				const frequencyMatch =
					diagnostics.frequencyMatch || diagnostics.frequency_match;
				return (
					typeof frequencyMatch?.reviewChallenge === 'string' &&
					frequencyMatch.reviewChallenge.trim().length > 0
				);
			});
			const createResponse = await createOrders({
				// UI-only diagnostics must never be persisted as order dynamic data.
				// The row id remains transport metadata; the API strips it before persistence.
				orders: prepareImportOrdersForAssignment(assignedOrders, {
					// Signed service selections and creation cohorts are bound to the
					// original Excel value/activity kept in transport diagnostics. The
					// API strips diagnostics before persistence.
					preserveDiagnostics:
						reviewedServiceReviewSelections.length > 0 ||
						reviewedContractFrequencyReviewSelections.length > 0 ||
						finalServiceCreationSelections.length > 0 ||
						refreshedFieldReviewSnapshot.length > 0 ||
						hasSignedDateDefaults ||
						hasSealedServiceReviewSession ||
						hasContractFrequencyReviewChallenge,
				}),
				...assignmentOptions,
				assignmentReviewSnapshot,
				...(refreshedFieldReviewSnapshot.length
					? { fieldReviewSnapshot: refreshedFieldReviewSnapshot }
					: {}),
				...(finalZoneCreationSelections.length
					? {
							zoneCreationSelections: finalZoneCreationSelections,
							approveZoneCreationChanges: true,
						}
					: {}),
				...(finalServiceCreationSelections.length
					? {
							serviceCreationSelections: finalServiceCreationSelections,
							approveServiceCreationChanges: true,
						}
					: {}),
				...(reviewedServiceReviewSelections.length
					? { serviceReviewSelections: reviewedServiceReviewSelections }
					: {}),
				...(reviewedContractFrequencyReviewSelections.length
					? {
							contractFrequencyReviewSelections:
								reviewedContractFrequencyReviewSelections,
						}
					: {}),
				...(finalContractFrequencyCreationSelections.length
					? {
							contractFrequencyCreationSelections:
								finalContractFrequencyCreationSelections,
							approveContractFrequencyCreationChanges: true,
						}
					: {}),
				...((approvalMatchesReviewedSnapshot &&
					pendingConfirmation?.assignmentReviewRequired) ||
				(refreshedApprovalRequired && reviewedApprovalRequired)
					? { approveAssignmentChanges: true }
					: {}),
				...(refreshedCoverageChangeCount > 0
					? { approveZoneCoverageChanges: true }
					: {}),
			});
			if (handlePartialCreateResult(createResponse)) return;

			setZoneCreationSelections([]);
			setServiceCreationSelections([]);
			setServiceReviewSelections([]);
			setAssignmentReviewSelections([]);
			setContractFrequencyReviewSelections([]);
			setContractFrequencyCreationRequests([]);
			setContractFrequencyCreationSelections([]);
			setFieldReviewSelections([]);
			setFieldReviewSnapshot([]);
			setPendingConfirmation(undefined);
			onResetOrders();
			sendToast({ message: 'Órdenes importadas!', type: 'success' });
		} catch (err) {
			if (handleMultiDomainStaleReviewResult(err)) return;
			if (handleStaleContractFrequencyResult(err)) return;
			if (handleStaleFieldReviewResult(err)) return;
			if (handleStaleAssignmentSelectionResult(err)) return;
			if (handleStaleServiceReviewResult(err)) return;
			if (handleStaleZoneCreationResult(err)) return;
			if (handlePartialCreateResult(err)) return;
			const assignmentReviewError = getAssignmentReviewErrorState(
				err,
				reviewOrders,
				assignedTechIdField,
			);
			if (assignmentReviewError) {
				const mergedOrders = Array.isArray(err?.orders)
					? mergeImportDiagnostics(err.orders, reviewOrders)
					: reviewOrders;
				const authoritativeOrders = applyAssignmentReviewSnapshot(
					mergedOrders,
					err?.assignmentReviewSnapshot,
				);
				const reviewableOrders =
					assignmentReviewError.kind === 'assignment_changed'
						? markAssignmentDrift(
								authoritativeOrders,
								reviewOrders,
								assignedTechIdField,
							)
						: authoritativeOrders;
				onSetState({ orders: reviewableOrders });
				setPendingConfirmation({
					assignmentFingerprint: getAssignmentSnapshotFingerprint(
						reviewableOrders,
						assignedTechIdField,
					),
					assignmentProofFingerprint: getAssignmentReviewProofFingerprint(
						reviewableOrders,
						assignedTechIdField,
					),
					coverageFingerprint: coverageFingerprint(reviewableOrders),
					zoneCreationFingerprint:
						getZoneCreationSnapshotFingerprint(reviewableOrders),
					assignmentChanged:
						assignmentReviewError.kind === 'assignment_changed',
					assignmentReviewRequired: true,
					coverageChanged: false,
					zoneCreationChanged: false,
				});
				sendToast({
					message:
						assignmentReviewError.kind === 'assignment_changed'
							? 'La asignación cambió antes de guardar. No se importó nada; revisa el técnico anterior y el nuevo, y confirma nuevamente.'
							: assignmentReviewError.kind === 'challenge_invalid'
								? 'La revisión de asignación venció o dejó de ser válida; revisa y confirma nuevamente.'
								: err?.message ||
									'La asignación recalculada está pendiente de confirmación. Revisa el resultado y confirma nuevamente la importación.',
					type: 'warn',
				});
			} else if (hasErrorCode(err, 'ZONE_COVERAGE_PROVISION_FAILED')) {
				if (Array.isArray(err?.orders)) {
					onSetState({
						orders: mergeImportDiagnostics(err.orders, reviewOrders),
					});
				}
				sendToast({
					message:
						'No se importaron las órdenes porque no fue posible aplicar el aprovisionamiento de cobertura. Revisa el plan actualizado antes de reintentar.',
					type: 'warn',
				});
			} else if (
				hasErrorCode(err, 'ZONE_COVERAGE_APPROVAL_REQUIRED') &&
				Array.isArray(err?.orders)
			) {
				onSetState({
					orders: mergeImportDiagnostics(err.orders, reviewOrders),
				});
				sendToast({
					message:
						'La cobertura cambió durante la validación. Revisa el plan actualizado y confirma nuevamente la importación.',
					type: 'warn',
				});
			} else {
				showImportError(
					err,
					'No fue posible importar las órdenes. Revisa las incidencias e inténtalo nuevamente.',
				);
			}
		} finally {
			busyRef.current = false;
			setIsSubmitting(false);
		}
	};

	const clearZoneCreationPlans = (plans, sourceOrders = reviewOrders) => {
		if (!plans.length) return sourceOrders;
		return sourceOrders.map(order =>
			plans.reduce(
				(updatedOrder, selection) =>
					clearZoneCreationSelection({ order: updatedOrder, selection }),
				order,
			),
		);
	};

	const onSelectZoneCreation = option => {
		const authoritativeOption = reviewOrders
			.map(order => getZoneCreationOption(order, option?.fieldId))
			.find(candidate => candidate?.challenge === option?.challenge);
		if (
			isBackgroundBlocked ||
			!authoritativeOption?.challenge ||
			!authoritativeOption?.fieldId ||
			!Array.isArray(authoritativeOption?.targetRowIds) ||
			!isZoneCreationSelectionAllowed(reviewOrders, authoritativeOption)
		) {
			if (option?.challenge)
				sendToast({
					message:
						'La autorización para crear la zona ya no está vigente. Valida los cambios antes de seleccionarla nuevamente.',
					type: 'warn',
				});
			return;
		}
		const targetRowIds = new Set(authoritativeOption.targetRowIds.map(String));
		onSetState({
			orders: reviewOrders.map(order =>
				targetRowIds.has(String(getImportRowIdentity(order)))
					? markOrderForRevalidation(
							invalidateAssignmentCandidateCapabilities(order),
						)
					: order,
			),
		});
		setZoneCreationSelections(current => [
			...current.filter(
				selection =>
					selection.fieldId !== authoritativeOption.fieldId ||
					(selection.sourceKey || selection.challenge) !==
						(authoritativeOption.sourceKey || authoritativeOption.challenge),
			),
			{ ...authoritativeOption, validated: false },
		]);
		setAssignmentReviewSelections(current =>
			current.filter(selection => !targetRowIds.has(String(selection.rowId))),
		);
		setPendingConfirmation(undefined);
	};

	const onUndoZoneCreation = option => {
		if (isBackgroundBlocked || !option?.challenge) return;
		const plans = zoneCreationSelections.filter(
			selection => selection.challenge === option.challenge,
		);
		const effectivePlans = plans.length ? plans : [option];
		const targetRowIds = new Set(
			effectivePlans.flatMap(plan => (plan.targetRowIds || []).map(String)),
		);
		const updatedOrders = clearZoneCreationPlans(effectivePlans).map(order =>
			targetRowIds.has(String(getImportRowIdentity(order)))
				? markOrderForRevalidation(
						invalidateAssignmentCandidateCapabilities(order),
					)
				: order,
		);
		if (targetRowIds.size > 0) onSetState({ orders: updatedOrders });
		setZoneCreationSelections(current =>
			current.filter(selection => selection.challenge !== option.challenge),
		);
		setAssignmentReviewSelections(current =>
			current.filter(selection => !targetRowIds.has(String(selection.rowId))),
		);
		setPendingConfirmation(undefined);
	};

	const onCancelImport = () => {
		if (isBackgroundBlocked) return;
		setZoneCreationSelections([]);
		setServiceCreationSelections([]);
		setServiceReviewSelections([]);
		setAssignmentReviewSelections([]);
		setContractFrequencyReviewSelections([]);
		setContractFrequencyCreationRequests([]);
		setContractFrequencyCreationSelections([]);
		setFieldReviewSelections([]);
		setFieldReviewSnapshot([]);
		setSelectedBulkRowIds([]);
		setPendingConfirmation(undefined);
		onResetOrders();
	};

	const onRequestFrequencyCreation = ({ rowData, draft, baseArtifact }) => {
		if (isBackgroundBlocked) return;
		const rowIdentity = getImportRowIdentity(rowData);
		const authoritativeOrder = reviewOrders.find(
			order =>
				rowIdentity !== undefined &&
				rowIdentity !== null &&
				String(getImportRowIdentity(order)) === String(rowIdentity),
		);
		try {
			const currentPlan = getContractFrequencyCreationPlansFromOrders([
				authoritativeOrder,
			])[0];
			const currentRequest = contractFrequencyCreationRequests.find(
				request => request.cohortKey === baseArtifact?.cohortKey,
			);
			const authoritativeBase = baseArtifact
				? currentRequest ||
					(currentPlan?.challenge === baseArtifact?.challenge
						? currentPlan
						: undefined)
				: undefined;
			if (baseArtifact && !authoritativeBase)
				throw new Error(
					'El plan cambió antes de editarse. Valida nuevamente la importación.',
				);
			const request = buildContractFrequencyCreationRequest({
				orders: reviewOrders,
				sourceOrder: authoritativeOrder,
				draft,
				baseArtifact: authoritativeBase,
			});
			const targetRowIds = new Set(request.targetRowIds.map(String));
			const previousSelection = contractFrequencyCreationSelections.find(
				selection => selection.cohortKey === request.cohortKey,
			);
			if (previousSelection)
				onSetState({
					orders: reviewOrders.map(order =>
						targetRowIds.has(String(getImportRowIdentity(order)))
							? setContractFrequencyCreationSelected({
									order,
									selection: previousSelection,
									selected: false,
								})
							: order,
					),
				});
			setContractFrequencyCreationRequests(current => [
				...current.filter(item => item.cohortKey !== request.cohortKey),
				request,
			]);
			setContractFrequencyCreationSelections(current =>
				current.filter(item => item.cohortKey !== request.cohortKey),
			);
			setContractFrequencyReviewSelections(current =>
				current.filter(selection => !targetRowIds.has(String(selection.rowId))),
			);
			setPendingConfirmation(undefined);
			sendToast({
				message:
					'Plan de frecuencia preparado. Pulsa “Validar cambios” para comprobarlo antes de agregarlo.',
				type: 'info',
			});
		} catch (error) {
			sendToast({ message: error.message, type: 'warn' });
		}
	};

	const onDiscardFrequencyCreationRequest = request => {
		if (isBackgroundBlocked || !request?.cohortKey) return;
		setContractFrequencyCreationRequests(current =>
			current.filter(item => item.cohortKey !== request.cohortKey),
		);
		setPendingConfirmation(undefined);
	};

	const onSelectFrequencyCreation = option => {
		if (isBackgroundBlocked || !option?.challenge) return;
		const authoritativePlan = getContractFrequencyCreationPlansFromOrders(
			reviewOrders,
		).find(plan => plan.challenge === option.challenge);
		if (!authoritativePlan) {
			sendToast({
				message:
					'El plan de frecuencia ya no tiene una autorización vigente. Valida nuevamente.',
				type: 'warn',
			});
			return;
		}
		const targetRowIds = new Set(authoritativePlan.targetRowIds.map(String));
		onSetState({
			orders: reviewOrders.map(order =>
				targetRowIds.has(String(getImportRowIdentity(order)))
					? setContractFrequencyCreationSelected({
							order,
							selection: authoritativePlan,
							selected: true,
						})
					: order,
			),
		});
		setContractFrequencyCreationRequests(current =>
			current.filter(item => item.cohortKey !== authoritativePlan.cohortKey),
		);
		setContractFrequencyCreationSelections(current => [
			...current.filter(item => item.cohortKey !== authoritativePlan.cohortKey),
			{ ...authoritativePlan, validated: false },
		]);
		setContractFrequencyReviewSelections(current =>
			current.filter(selection => !targetRowIds.has(String(selection.rowId))),
		);
		setPendingConfirmation(undefined);
		sendToast({
			message:
				'Frecuencia agregada al plan. Pulsa “Validar cambios” antes de importar.',
			type: 'info',
		});
	};

	const onUndoFrequencyCreation = option => {
		if (isBackgroundBlocked || !option?.challenge) return;
		const selection = contractFrequencyCreationSelections.find(
			item => item.challenge === option.challenge,
		);
		if (!selection) return;
		const targetRowIds = new Set(selection.targetRowIds.map(String));
		onSetState({
			orders: reviewOrders.map(order =>
				targetRowIds.has(String(getImportRowIdentity(order)))
					? setContractFrequencyCreationSelected({
							order,
							selection,
							selected: false,
						})
					: order,
			),
		});
		setContractFrequencyCreationSelections(current =>
			current.filter(item => item.challenge !== selection.challenge),
		);
		setPendingConfirmation(undefined);
	};

	const onChangeServiceHandler = ({
		option,
		rowData,
		selected = { items: [] },
		applyToSimilar = false,
		confirmationHandled = false,
	}) => {
		if (
			(isBackgroundBlocked && !confirmationHandled) ||
			!serviceIdField ||
			!option ||
			isExistingImportOrder(rowData)
		)
			return;
		const rowIdentity = getImportRowIdentity(rowData);
		const availableRowIds = new Set(
			reviewOrders.map(order => String(getImportRowIdentity(order))),
		);
		const selectedIds = new Set(
			(selected.items || [])
				.filter(id => id !== undefined && id !== null && String(id) !== '')
				.map(String)
				.filter(id => id !== String(rowIdentity) && availableRowIds.has(id)),
		);
		const cohortKey = getServiceCohortKey(rowData);
		const targetOrders = (
			applyToSimilar
				? getServiceCohortTargets(reviewOrders, rowData)
				: reviewOrders.filter(order => {
						const orderIdentity = getImportRowIdentity(order);
						return (
							String(orderIdentity) === String(rowIdentity) ||
							selectedIds.has(String(orderIdentity))
						);
					})
		).filter(order => !isExistingImportOrder(order));
		const explicitTargetRowIds = targetOrders.map(order =>
			String(getImportRowIdentity(order)),
		);
		if (
			selectedIds.size > 0 &&
			!confirmationHandled &&
			(typeof window.confirm !== 'function' ||
				!window.confirm(
					`Se aplicará el servicio únicamente a estas filas seleccionadas: ${explicitTargetRowIds.join(
						', ',
					)}. ¿Deseas continuar?`,
				))
		)
			return;
		const incompatibleOrder = applyToSimilar
			? targetOrders.find(order => {
					const service = getServicePresentation(order);
					const hasManualDecision =
						service.strategy === 'manual' &&
						service.assigned &&
						String(service.assigned.id) !== String(option.id);
					const serviceCreation =
						order?.importDiagnostics?.serviceCreation ||
						order?.importDiagnostics?.service_creation ||
						{};
					const hasCreationDecision =
						service.strategy === 'create_new' ||
						Boolean(serviceCreation.request) ||
						serviceCreation.status === 'selected' ||
						serviceCreation.selected === true ||
						serviceCreationSelections.some(selection =>
							isOrderTargetedByServiceCreation(order, selection),
						);
					return hasManualDecision || hasCreationDecision;
				})
			: undefined;
		if (incompatibleOrder) {
			sendToast({
				message: `No se aplicó ningún cambio. La fila ${getImportRowIdentity(
					incompatibleOrder,
				)} ya tiene una decisión de servicio incompatible. Revísala antes de aplicar a valores iguales.`,
				type: 'warn',
			});
			return;
		}
		const signedSelections = targetOrders.map(order => {
			const candidate = getServicePresentation(order).candidates.find(
				item => String(item.id) === String(option.id),
			);
			return candidate?.selectionChallenge
				? {
						challenge: candidate.selectionChallenge,
						rowId: String(getImportRowIdentity(order)),
						cohortKey: getServiceCohortKey(order),
					}
				: undefined;
		});
		if (signedSelections.some(selection => !selection)) {
			sendToast({
				message: applyToSimilar
					? 'No se aplicó ningún cambio. Al menos una orden igual no tiene el candidato firmado requerido; valida nuevamente la importación.'
					: 'La opción de servicio necesita una validación nueva. Vuelve a abrir la revisión antes de aplicarla.',
				type: 'warn',
			});
			return;
		}
		const targetIds = new Set(
			targetOrders.map(order => String(getImportRowIdentity(order))),
		);
		const invalidatedZonePlans = zoneCreationSelections.filter(selection =>
			(selection.targetRowIds || []).some(rowId =>
				targetIds.has(String(rowId)),
			),
		);
		const invalidatedServicePlans = serviceCreationSelections.filter(
			selection =>
				(selection.targetRowIds || []).some(rowId =>
					targetIds.has(String(rowId)),
				),
		);
		const crossesSelection = [
			...invalidatedZonePlans,
			...invalidatedServicePlans,
		].some(selection =>
			(selection.targetRowIds || []).some(
				rowId => !targetIds.has(String(rowId)),
			),
		);
		if (crossesSelection) {
			sendToast({
				message:
					'No se aplicó ningún cambio. Un plan de creación pendiente también afecta filas fuera de la selección; deshaz ese plan o selecciona todas sus filas.',
				type: 'warn',
			});
			return false;
		}
		const sourceOrders = clearZoneCreationPlans(invalidatedZonePlans);
		const updatedOrders = sourceOrders.map(order => {
			const targeted = targetIds.has(String(getImportRowIdentity(order)));
			const orderWithoutCreation = invalidatedServicePlans.reduce(
				(currentOrder, selection) =>
					isOrderTargetedByServiceCreation(currentOrder, selection)
						? setServiceCreationSelected({
								order: currentOrder,
								selection,
								selected: false,
							})
						: currentOrder,
				order,
			);
			const currentService = getServicePresentation(order);
			const alreadySelected =
				applyToSimilar &&
				currentService.strategy === 'manual' &&
				String(currentService.assigned?.id) === String(option.id);
			const serviceOrder = targeted
				? alreadySelected
					? orderWithoutCreation
					: applyManualServiceSelection({
							order: orderWithoutCreation,
							option,
							serviceIdField,
						})
				: orderWithoutCreation;
			return targeted
				? invalidateAssignmentCandidateCapabilities(serviceOrder)
				: serviceOrder;
		});
		onSetState({ orders: updatedOrders });
		setServiceReviewSelections(current => {
			const byRowId = new Map(
				current.map(selection => [selection.rowId, selection]),
			);
			signedSelections.forEach(selection =>
				byRowId.set(selection.rowId, selection),
			);
			return Array.from(byRowId.values());
		});
		setServiceCreationSelections(current =>
			current.filter(
				selection =>
					!invalidatedServicePlans.includes(selection) &&
					(confirmationHandled || selection.cohortKey !== cohortKey),
			),
		);
		if (invalidatedZonePlans.length)
			setZoneCreationSelections(current =>
				current.filter(selection => !invalidatedZonePlans.includes(selection)),
			);
		setAssignmentReviewSelections(current =>
			current.filter(selection => !targetIds.has(String(selection.rowId))),
		);
		setPendingConfirmation(undefined);
		if (applyToSimilar) {
			const targetCount = targetOrders.length;
			sendToast({
				message: `Servicio aplicado localmente a ${targetCount} ${
					targetCount === 1 ? 'orden' : 'órdenes'
				}. Pulsa “Validar cambios” para confirmar y actualizar las incidencias.`,
				type: 'info',
			});
		}
		return confirmationHandled ? true : undefined;
	};

	const onStageLegacyService = ({
		option,
		rowData,
		field = serviceIdField,
	}) => {
		if (isBackgroundBlocked || isExistingImportOrder(rowData)) return;
		const sourceValue = String(option?.name ?? '').trim();
		const serviceFieldId = serviceIdField?.id;
		const serviceFieldName = String(serviceIdField?.name ?? '').trim();
		const rowIdentity = getImportRowIdentity(rowData);
		if (
			serviceFieldId === undefined ||
			serviceFieldId === null ||
			!serviceFieldName ||
			String(field?.id) !== String(serviceFieldId) ||
			!sourceValue ||
			rowIdentity === undefined ||
			rowIdentity === null
		) {
			sendToast({
				message:
					'No fue posible preparar este servicio para validación. Selecciona una opción con nombre válido e inténtalo nuevamente.',
				type: 'warn',
			});
			return;
		}
		let updated = false;
		const updatedOrders = reviewOrders.map(order => {
			if (String(getImportRowIdentity(order)) !== String(rowIdentity))
				return order;
			updated = true;
			const diagnostics = getImportDiagnostics(order);
			return markOrderForRevalidation(
				invalidateAssignmentCandidateCapabilities({
					...order,
					[serviceFieldId]: sourceValue,
					[serviceFieldName]: sourceValue,
					importDiagnostics: {
						...diagnostics,
						sourceFieldValues: {
							...(diagnostics.sourceFieldValues || {}),
							[serviceFieldId]: sourceValue,
						},
					},
				}),
			);
		});
		if (!updated) {
			sendToast({
				message:
					'La fila ya no está disponible en esta importación. Vuelve a abrir la revisión.',
				type: 'warn',
			});
			return;
		}
		const frequencyInvalidatedOrders = updatedOrders.map(order => {
			const orderId = String(getImportRowIdentity(order));
			const frequency = getContractFrequencyPresentation(order);
			const hasFrequencySelection = contractFrequencyReviewSelections.some(
				selection => String(selection.rowId) === orderId,
			);
			if (
				!hasFrequencySelection &&
				frequency.mutable !== true &&
				frequency.candidates.length === 0
			)
				return order;
			return markOrderForRevalidation(
				invalidateContractFrequencyCapabilities(order),
			);
		});
		onSetState({ orders: frequencyInvalidatedOrders });
		setContractFrequencyReviewSelections([]);
		setServiceReviewSelections(current =>
			current.filter(
				selection => String(selection.rowId) !== String(rowIdentity),
			),
		);
		setAssignmentReviewSelections(current =>
			current.filter(
				selection => String(selection.rowId) !== String(rowIdentity),
			),
		);
		setPendingConfirmation(undefined);
		sendToast({
			message:
				'Servicio preparado localmente y pendiente de validar. Pulsa “Validar cambios” para confirmar la asignación.',
			type: 'info',
		});
	};

	const onRequestServiceCreation = ({ rowData, name, archetypeServiceId }) => {
		if (isBackgroundBlocked || isExistingImportOrder(rowData)) return;
		const rowIdentity = getImportRowIdentity(rowData);
		try {
			const updatedOrders = reviewOrders.map(order =>
				String(getImportRowIdentity(order)) === String(rowIdentity)
					? markOrderForRevalidation(
							invalidateAssignmentCandidateCapabilities(
								applyServiceCreationRequest({
									order,
									name,
									archetypeServiceId,
									targetRowIds: [rowIdentity],
								}),
							),
						)
					: order,
			);
			onSetState({ orders: updatedOrders });
			setServiceCreationSelections([]);
			setAssignmentReviewSelections(current =>
				current.filter(
					selection => String(selection.rowId) !== String(rowIdentity),
				),
			);
			setPendingConfirmation(undefined);
		} catch (error) {
			sendToast({ message: error.message, type: 'warn' });
		}
	};

	const onUndoServiceSelection = rowData => {
		if (
			isBackgroundBlocked ||
			!serviceIdField ||
			isExistingImportOrder(rowData)
		)
			return;
		const cohortKey = getServiceCohortKey(rowData);
		const updatedOrders = reviewOrders.map(order =>
			getServiceCohortKey(order) === cohortKey &&
			getServicePresentation(order).previous &&
			Object.keys(getServicePresentation(order).previous).length > 0
				? markOrderForRevalidation(
						invalidateAssignmentCandidateCapabilities(
							undoManualServiceSelection({ order, serviceIdField }),
						),
					)
				: order,
		);
		const undoneRowIds = new Set(
			reviewOrders
				.filter(
					order =>
						getServiceCohortKey(order) === cohortKey &&
						getServicePresentation(order).previous &&
						Object.keys(getServicePresentation(order).previous).length > 0,
				)
				.map(order => String(getImportRowIdentity(order))),
		);
		onSetState({ orders: updatedOrders });
		setServiceReviewSelections(current =>
			current.filter(selection => !undoneRowIds.has(String(selection.rowId))),
		);
		setServiceCreationSelections(current =>
			current.filter(selection => selection.cohortKey !== cohortKey),
		);
		setAssignmentReviewSelections(current =>
			current.filter(selection => !undoneRowIds.has(String(selection.rowId))),
		);
		setPendingConfirmation(undefined);
	};

	const onSelectServiceCreation = option => {
		if (isBackgroundBlocked || !option?.challenge || !option?.cohortKey) return;
		if (
			reviewOrders.some(
				order =>
					isExistingImportOrder(order) &&
					isOrderTargetedByServiceCreation(order, option),
			)
		)
			return;
		const targetRowIds = new Set((option.targetRowIds || []).map(String));
		onSetState({
			orders: reviewOrders.map(order => {
				const updatedOrder = setServiceCreationSelected({
					order,
					selection: option,
					selected: true,
				});
				return targetRowIds.has(String(getImportRowIdentity(order)))
					? markOrderForRevalidation(
							invalidateAssignmentCandidateCapabilities(updatedOrder),
						)
					: updatedOrder;
			}),
		});
		setServiceCreationSelections(current => [
			...current.filter(selection => selection.cohortKey !== option.cohortKey),
			{ ...option, validated: true },
		]);
		setAssignmentReviewSelections(current =>
			current.filter(selection => !targetRowIds.has(String(selection.rowId))),
		);
		setPendingConfirmation(undefined);
	};

	const onUndoServiceCreation = option => {
		if (isBackgroundBlocked || !option?.challenge) return;
		const targetRowIds = new Set((option.targetRowIds || []).map(String));
		onSetState({
			orders: reviewOrders.map(order => {
				const updatedOrder = setServiceCreationSelected({
					order,
					selection: option,
					selected: false,
				});
				return targetRowIds.has(String(getImportRowIdentity(order)))
					? markOrderForRevalidation(
							invalidateAssignmentCandidateCapabilities(updatedOrder),
						)
					: updatedOrder;
			}),
		});
		setServiceCreationSelections(current =>
			current.filter(selection => selection.challenge !== option.challenge),
		);
		setAssignmentReviewSelections(current =>
			current.filter(selection => !targetRowIds.has(String(selection.rowId))),
		);
		setPendingConfirmation(undefined);
	};

	const onChangeZoneHandler = ({
		option,
		rowData,
		fieldId,
		selected = { items: [] },
		applyToSimilar = false,
		bulkAction = false,
	}) => {
		if (isBackgroundBlocked && !bulkAction) return;
		const zoneIdField = zoneFields.find(field => field.id === fieldId);
		if (!cityIdField || !townshipIdField || !zoneIdField || !option) return;
		const selectedIds = new Set((selected?.items || []).map(String));
		const sourceValues = getSourceValues(rowData, fieldId);
		const sourceKeys = ['city', 'township', 'zone'].filter(
			key =>
				sourceValues[key] !== undefined &&
				sourceValues[key] !== null &&
				String(sourceValues[key]) !== '',
		);
		const isSameSource = order => {
			const candidateSource = getSourceValues(order, fieldId);
			return (
				sourceKeys.length > 0 &&
				sourceKeys.every(key => candidateSource[key] === sourceValues[key])
			);
		};
		const rowIdentity = getImportRowIdentity(rowData);
		const isTarget = order => {
			const orderIdentity = getImportRowIdentity(order);
			return (
				(rowIdentity !== undefined && orderIdentity === rowIdentity) ||
				(orderIdentity !== undefined &&
					selectedIds.has(String(orderIdentity))) ||
				(applyToSimilar && isSameSource(order))
			);
		};
		const targetedRowIds = new Set(
			reviewOrders
				.filter(isTarget)
				.map(getImportRowIdentity)
				.filter(identity => identity !== undefined)
				.map(String),
		);
		const invalidatedZonePlans = zoneCreationSelections.filter(
			selection =>
				(bulkAction || selection.fieldId === fieldId) &&
				(selection.targetRowIds || []).some(rowId =>
					targetedRowIds.has(String(rowId)),
				),
		);
		const assignmentContextRowIds = new Set(targetedRowIds);
		invalidatedZonePlans.forEach(selection =>
			(selection.targetRowIds || []).forEach(rowId =>
				assignmentContextRowIds.add(String(rowId)),
			),
		);
		const sourceOrders = clearZoneCreationPlans(invalidatedZonePlans);

		const updatedOrders = sourceOrders.map(order => {
			if (isTarget(order))
				return invalidateAssignmentCandidateCapabilities(
					applyManualLocationSelection({
						order,
						option,
						fieldId,
						zoneIdField,
						cityIdField,
						townshipIdField,
					}),
				);
			return assignmentContextRowIds.has(String(getImportRowIdentity(order)))
				? markOrderForRevalidation(
						invalidateAssignmentCandidateCapabilities(order),
					)
				: order;
		});

		onSetState({ orders: updatedOrders });
		if (invalidatedZonePlans.length)
			setZoneCreationSelections(current =>
				current.filter(selection => !invalidatedZonePlans.includes(selection)),
			);
		setAssignmentReviewSelections(current =>
			current.filter(
				selection => !assignmentContextRowIds.has(String(selection.rowId)),
			),
		);
		setPendingConfirmation(undefined);
		return bulkAction ? true : undefined;
	};

	const onChangeExpertHandler = ({ option, rowData }) => {
		if (isBackgroundBlocked || !assignedTechIdField) return;
		const rowIdentity = getImportRowIdentity(rowData);
		const selectedOrder = reviewOrders.find(
			order =>
				rowIdentity !== undefined &&
				String(getImportRowIdentity(order)) === String(rowIdentity),
		);
		const optionKey = getBulkAssignmentOptionKey(option);
		const candidate = getAssignmentPresentation(
			selectedOrder,
			assignedTechIdField,
		).candidates.find(
			item =>
				getBulkAssignmentOptionKey(item) === optionKey &&
				Boolean(item.selectionChallenge),
		);
		if (!selectedOrder || !candidate) {
			sendToast({
				message:
					'La opción de técnico no tiene una autorización vigente. Pulsa “Validar cambios” antes de seleccionarla.',
				type: 'warn',
			});
			return;
		}
		onStageAssignmentSelection({
			rowData: selectedOrder,
			option: {
				...candidate,
				id: candidate.assignedTechId,
			},
		});
	};

	const onStageAssignmentSelection = ({ option, rowData }) => {
		if (isBackgroundBlocked || !assignedTechIdField) return;
		const rowIdentity = getImportRowIdentity(rowData);
		const matchingOrders = reviewOrders.filter(
			order =>
				rowIdentity !== undefined &&
				getImportRowIdentity(order) === rowIdentity,
		);
		const selectedOrder =
			matchingOrders.length === 1 ? matchingOrders[0] : undefined;
		const presentation = getAssignmentPresentation(
			selectedOrder,
			assignedTechIdField,
		);
		const optionKey = getBulkAssignmentOptionKey(option);
		const candidate = presentation.candidates.find(
			item =>
				String(item.selectionChallenge) ===
					String(option?.selectionChallenge || '') &&
				getBulkAssignmentOptionKey(item) === optionKey,
		);
		if (
			!selectedOrder ||
			presentation.mutability.allowed !== true ||
			!candidate
		) {
			sendToast({
				message:
					'No fue posible verificar la selección del técnico. Valida nuevamente la importación.',
				type: 'warn',
			});
			return;
		}
		onSetState({
			orders: reviewOrders.map(order =>
				getImportRowIdentity(order) === rowIdentity
					? markOrderForRevalidation(order)
					: order,
			),
		});
		setAssignmentReviewSelections(current => [
			...current.filter(
				selection => String(selection.rowId) !== String(rowIdentity),
			),
			{
				rowId: String(rowIdentity),
				challenge: candidate.selectionChallenge,
				validated: false,
			},
		]);
		setPendingConfirmation(undefined);
		sendToast({
			message:
				'Selección de técnico preparada localmente. Pulsa “Validar cambios” para confirmarla.',
			type: 'info',
		});
	};

	const onApplyContractFrequency = ({ option, rowData }) => {
		if (
			busyRef.current ||
			isBackgroundBlocked ||
			!contractFrequencyIdField ||
			contractFrequencyIdField.show !== true
		)
			return;
		const rowIdentity = getImportRowIdentity(rowData);
		const matchingOrders = reviewOrders.filter(
			order =>
				rowIdentity !== undefined &&
				rowIdentity !== null &&
				String(getImportRowIdentity(order)) === String(rowIdentity),
		);
		const selectedOrder =
			matchingOrders.length === 1 ? matchingOrders[0] : undefined;
		const frequency = getContractFrequencyPresentation(selectedOrder);
		const candidate = frequency.candidates.find(
			item =>
				String(item.frequencyId) === String(option?.frequencyId) &&
				item.selectionChallenge === option?.selectionChallenge,
		);
		if (!selectedOrder || frequency.mutable !== true || !candidate) {
			sendToast({
				message:
					'La opción de frecuencia no tiene una autorización vigente. Valida nuevamente la importación.',
				type: 'warn',
			});
			return;
		}

		const stagedOrder = applyContractFrequencySelection({
			order: selectedOrder,
			option: candidate,
			field: contractFrequencyIdField,
		});
		if (stagedOrder === selectedOrder) {
			sendToast({
				message:
					'No fue posible verificar la selección de frecuencia. Valida nuevamente la importación.',
				type: 'warn',
			});
			return;
		}

		const creationInvalidation = getContractFrequencyCreationCohortInvalidation(
			[rowIdentity],
		);
		onSetState({
			orders: creationInvalidation.orders.map(order =>
				String(getImportRowIdentity(order)) === String(rowIdentity)
					? markOrderForRevalidation(
							invalidateContractFrequencyCreationCapabilities(stagedOrder),
						)
					: order,
			),
		});
		clearContractFrequencyCreationCohorts(creationInvalidation.cohortKeys);
		setContractFrequencyReviewSelections(current => {
			const previousSelection = current.find(
				selection => String(selection.rowId) === String(rowIdentity),
			);
			return [
				...current.filter(
					selection => String(selection.rowId) !== String(rowIdentity),
				),
				{
					rowId: String(rowIdentity),
					frequencyId: candidate.frequencyId,
					challenge: candidate.selectionChallenge,
					validated: false,
					previousState:
						previousSelection?.previousState ||
						captureContractFrequencyState(
							selectedOrder,
							contractFrequencyIdField,
						),
				},
			];
		});
		setPendingConfirmation(undefined);
		sendToast({
			message:
				'Frecuencia preparada localmente. Pulsa “Validar cambios” para confirmar la selección.',
			type: 'info',
		});
	};

	const getExactBulkTargets = targetRowIds => {
		const requestedIds = Array.from(
			new Set(
				(targetRowIds || [])
					.filter(rowId => rowId !== undefined && rowId !== null)
					.map(String),
			),
		);
		const targets = getSelectedImportOrders(reviewOrders, requestedIds);
		return requestedIds.length > 0 && targets.length === requestedIds.length
			? targets
			: [];
	};

	const showStaleBulkAction = label => {
		sendToast({
			message: `No se aplicó ningún cambio de ${label}. La selección dejó de ser compatible; valida nuevamente o reduce las filas seleccionadas.`,
			type: 'warn',
		});
	};

	const onApplyBulkFrequency = ({ option, targetRowIds }) => {
		if (
			busyRef.current ||
			isBusy ||
			!contractFrequencyIdField ||
			contractFrequencyIdField.show !== true ||
			!getBulkContractFrequencyOptionKey(option)
		)
			return false;
		const targets = getExactBulkTargets(targetRowIds);
		if (!targets.length) {
			showStaleBulkAction('frecuencia');
			return false;
		}
		const creationInvalidation =
			getContractFrequencyCreationCohortInvalidation(targetRowIds);
		const prepared = prepareBulkContractFrequencyTransition({
			orders: creationInvalidation.orders,
			transition: { ...option, targetRowIds },
			field: contractFrequencyIdField,
			existingSelections: contractFrequencyReviewSelections,
		});
		if (!prepared.ok) {
			sendToast({ message: prepared.error, type: 'warn' });
			return false;
		}
		const targetIds = new Set(prepared.targetRowIds.map(String));
		onSetState({
			orders: prepared.orders.map(order =>
				targetIds.has(String(getImportRowIdentity(order)))
					? markOrderForRevalidation(
							invalidateContractFrequencyCreationCapabilities(order),
						)
					: order,
			),
		});
		clearContractFrequencyCreationCohorts(creationInvalidation.cohortKeys);
		setContractFrequencyReviewSelections(prepared.selections);
		setPendingConfirmation(undefined);
		return true;
	};

	const onApplyBulkAssignment = ({ option, targetRowIds }) => {
		if (isBusy || !assignedTechIdField) return false;
		const targets = getExactBulkTargets(targetRowIds);
		const optionKey = getBulkAssignmentOptionKey(option);
		const compatibleOption = getBulkAssignmentOptions(
			targets,
			assignedTechIdField,
		).options.find(
			candidate => getBulkAssignmentOptionKey(candidate) === optionKey,
		);
		if (!targets.length || !compatibleOption) {
			showStaleBulkAction('técnico');
			return false;
		}
		const targetIds = new Set(
			targets.map(order => String(getImportRowIdentity(order))),
		);
		const selections = targets.map(order => {
			const candidate = getAssignmentPresentation(
				order,
				assignedTechIdField,
			).candidates.find(item => getBulkAssignmentOptionKey(item) === optionKey);
			return {
				rowId: String(getImportRowIdentity(order)),
				challenge: candidate.selectionChallenge,
				validated: false,
			};
		});
		onSetState({
			orders: reviewOrders.map(order =>
				targetIds.has(String(getImportRowIdentity(order)))
					? markOrderForRevalidation(order)
					: order,
			),
		});
		setAssignmentReviewSelections(current => {
			const byRowId = new Map(
				current.map(selection => [String(selection.rowId), selection]),
			);
			selections.forEach(selection => byRowId.set(selection.rowId, selection));
			return Array.from(byRowId.values());
		});
		setPendingConfirmation(undefined);
		return true;
	};

	const onApplyBulkService = ({ option, targetRowIds }) => {
		if (isBusy || !serviceIdField) return false;
		const targets = getExactBulkTargets(targetRowIds);
		const optionKey = getBulkServiceOptionKey(option);
		const compatibleOption = getBulkServiceOptions(targets).options.find(
			candidate => getBulkServiceOptionKey(candidate) === optionKey,
		);
		if (!targets.length || !compatibleOption) {
			showStaleBulkAction('servicio');
			return false;
		}
		return (
			onChangeServiceHandler({
				option: compatibleOption,
				rowData: targets[0],
				selected: { items: targetRowIds },
				confirmationHandled: true,
			}) === true
		);
	};

	const onApplyBulkZone = ({ option, fieldId, targetRowIds }) => {
		if (isBusy) return false;
		const targets = getExactBulkTargets(targetRowIds);
		const zoneIdField = zoneFields.find(field => field.id === fieldId);
		const optionKey = getBulkLocationOptionKey(option);
		const compatibleOption = getBulkLocationOptions(
			targets,
			locationFields,
			zoneIdField,
		).options.find(
			candidate => getBulkLocationOptionKey(candidate) === optionKey,
		);
		if (!targets.length || !compatibleOption) {
			showStaleBulkAction('zona');
			return false;
		}
		const targetIds = new Set(
			targets.map(order => String(getImportRowIdentity(order))),
		);
		const crossesSelection = zoneCreationSelections.some(
			selection =>
				(selection.targetRowIds || []).some(rowId =>
					targetIds.has(String(rowId)),
				) &&
				(selection.targetRowIds || []).some(
					rowId => !targetIds.has(String(rowId)),
				),
		);
		if (crossesSelection) {
			sendToast({
				message:
					'No se aplicó ningún cambio. Una creación de zona pendiente también afecta filas fuera de la selección; deshaz ese plan o selecciona todas sus filas.',
				type: 'warn',
			});
			return false;
		}
		return (
			onChangeZoneHandler({
				option: compatibleOption,
				rowData: targets[0],
				fieldId,
				selected: { items: targetRowIds },
				bulkAction: true,
			}) === true
		);
	};

	const onApplyFieldReview = ({ rowData, editor, value }) => {
		if (isBackgroundBlocked) return;
		const rowIdentity = getImportRowIdentity(rowData);
		const authoritativeOrder = reviewOrders.find(
			order => String(getImportRowIdentity(order)) === String(rowIdentity),
		);
		const authoritativeEditor = getFieldReviewDescriptors(
			authoritativeOrder,
		).find(
			candidate =>
				candidate.fieldId === editor?.fieldId &&
				candidate.selectionChallenge === editor?.selectionChallenge,
		);
		if (!authoritativeOrder || !authoritativeEditor) {
			sendToast({
				message:
					'Este campo ya no tiene una autorización válida para editarse. Valida nuevamente la importación.',
				type: 'warn',
			});
			return;
		}
		const normalizedValue = normalizeFieldReviewValue(
			value,
			authoritativeEditor,
		);
		if (authoritativeEditor.required && !normalizedValue) {
			sendToast({
				message: `${authoritativeEditor.label} es obligatorio.`,
				type: 'warn',
			});
			return;
		}
		const invalidatesAssignment = changesAssignmentContext(
			authoritativeEditor.fieldId,
		);
		const invalidatesFrequency = changesContractFrequencyContext(
			authoritativeEditor.fieldId,
		);
		const creationInvalidation = invalidatesFrequency
			? getContractFrequencyCreationCohortInvalidation([rowIdentity])
			: undefined;
		onSetState({
			orders: (creationInvalidation?.orders || reviewOrders).map(order => {
				if (String(getImportRowIdentity(order)) !== String(rowIdentity))
					return order;
				const updatedOrder = {
					...order,
					[authoritativeEditor.fieldId]: normalizedValue,
				};
				const assignmentOrder = invalidatesAssignment
					? invalidateAssignmentCandidateCapabilities(updatedOrder)
					: updatedOrder;
				return markOrderForRevalidation(
					invalidatesFrequency
						? invalidateContractFrequencyCapabilities(assignmentOrder)
						: assignmentOrder,
				);
			}),
		});
		setFieldReviewSelections(current => [
			...current.filter(
				selection =>
					String(selection.rowId) !== String(rowIdentity) ||
					selection.fieldId !== authoritativeEditor.fieldId,
			),
			{
				rowId: String(rowIdentity),
				fieldId: authoritativeEditor.fieldId,
				issueCode: authoritativeEditor.issueCode,
				challenge: authoritativeEditor.selectionChallenge,
				value: normalizedValue,
				previousValue: authoritativeOrder[authoritativeEditor.fieldId],
				sourceCategory: authoritativeEditor.sourceCategory,
			},
		]);
		setFieldReviewSnapshot(current =>
			current.filter(
				proof =>
					String(proof.rowId) !== String(rowIdentity) ||
					String(proof.fieldId) !== authoritativeEditor.fieldId,
			),
		);
		if (invalidatesAssignment)
			setAssignmentReviewSelections(current =>
				current.filter(
					candidate => String(candidate.rowId) !== String(rowIdentity),
				),
			);
		if (invalidatesFrequency)
			setContractFrequencyReviewSelections(current =>
				current.filter(
					candidate => String(candidate.rowId) !== String(rowIdentity),
				),
			);
		clearContractFrequencyCreationCohorts(creationInvalidation?.cohortKeys);
		setPendingConfirmation(undefined);
		sendToast({
			message: `${authoritativeEditor.label} quedó pendiente de validar. Pulsa “Validar cambios” para confirmar.`,
			type: 'info',
		});
	};

	const onUndoFieldReview = ({ rowData, editor }) => {
		if (isBackgroundBlocked) return;
		const rowIdentity = getImportRowIdentity(rowData);
		const selection = fieldReviewSelections.find(
			candidate =>
				String(candidate.rowId) === String(rowIdentity) &&
				candidate.fieldId === editor?.fieldId,
		);
		if (!selection) return;
		const invalidatesAssignment = changesAssignmentContext(selection.fieldId);
		const invalidatesFrequency = changesContractFrequencyContext(
			selection.fieldId,
		);
		const creationInvalidation = invalidatesFrequency
			? getContractFrequencyCreationCohortInvalidation([rowIdentity])
			: undefined;
		onSetState({
			orders: (creationInvalidation?.orders || reviewOrders).map(order => {
				if (String(getImportRowIdentity(order)) !== String(rowIdentity))
					return order;
				const restoredOrder = restoreStagedFieldReviewValue(order, selection);
				const assignmentOrder = invalidatesAssignment
					? invalidateAssignmentCandidateCapabilities(restoredOrder)
					: restoredOrder;
				return invalidatesFrequency
					? markOrderForRevalidation(
							invalidateContractFrequencyCapabilities(assignmentOrder),
						)
					: assignmentOrder;
			}),
		});
		setFieldReviewSelections(current =>
			current.filter(
				candidate =>
					String(candidate.rowId) !== String(rowIdentity) ||
					candidate.fieldId !== selection.fieldId,
			),
		);
		setFieldReviewSnapshot(current =>
			current.filter(
				proof =>
					String(proof.rowId) !== String(rowIdentity) ||
					String(proof.fieldId) !== selection.fieldId,
			),
		);
		if (invalidatesAssignment)
			setAssignmentReviewSelections(current =>
				current.filter(
					candidate => String(candidate.rowId) !== String(rowIdentity),
				),
			);
		if (invalidatesFrequency)
			setContractFrequencyReviewSelections(current =>
				current.filter(
					candidate => String(candidate.rowId) !== String(rowIdentity),
				),
			);
		clearContractFrequencyCreationCohorts(creationInvalidation?.cohortKeys);
		setPendingConfirmation(undefined);
	};

	const onFieldTypeChange = (value, { rowData, field }) => {
		if (isBackgroundBlocked) return;
		const signedEditor = getFieldReviewDescriptors(rowData).find(
			editor => editor.fieldId === field.id,
		);
		if (signedEditor) {
			onApplyFieldReview({ rowData, editor: signedEditor, value });
			return;
		}
		const rowIdentity = getImportRowIdentity(rowData);
		const isLocationField = [
			cityIdField?.id,
			townshipIdField?.id,
			...zoneFields.map(zoneField => zoneField.id),
		]
			.filter(fieldId => fieldId !== undefined && fieldId !== null)
			.some(fieldId => String(fieldId) === String(field.id));
		const invalidatesAssignment = changesAssignmentContext(field.id);
		const invalidatesFrequency = changesContractFrequencyContext(field.id);
		const invalidatedZonePlans = isLocationField
			? zoneCreationSelections.filter(
					selection =>
						(selection.fieldId === field.id ||
							field.id === cityIdField?.id ||
							field.id === townshipIdField?.id) &&
						(selection.targetRowIds || [])
							.map(String)
							.includes(String(rowIdentity)),
				)
			: [];
		let sourceOrders = clearZoneCreationPlans(invalidatedZonePlans);
		const creationInvalidation = invalidatesFrequency
			? invalidateContractFrequencyCreationCohorts({
					orders: sourceOrders,
					requests: contractFrequencyCreationRequests,
					selections: contractFrequencyCreationSelections,
					changedRowIds: [rowIdentity],
				})
			: undefined;
		if (creationInvalidation) sourceOrders = creationInvalidation.orders;
		const updatedOrders = sourceOrders.map(order => {
			if (
				rowIdentity === undefined ||
				String(getImportRowIdentity(order)) !== String(rowIdentity)
			)
				return order;
			const updatedOrder = { ...order, [field.id]: value };
			const assignmentOrder = invalidatesAssignment
				? invalidateAssignmentCandidateCapabilities(updatedOrder)
				: updatedOrder;
			return markOrderForRevalidation(
				invalidatesFrequency
					? invalidateContractFrequencyCapabilities(assignmentOrder)
					: assignmentOrder,
			);
		});
		onSetState({ orders: updatedOrders });
		if (invalidatedZonePlans.length)
			setZoneCreationSelections(current =>
				current.filter(selection => !invalidatedZonePlans.includes(selection)),
			);
		if (invalidatesAssignment)
			setAssignmentReviewSelections(current =>
				current.filter(
					selection => String(selection.rowId) !== String(rowIdentity),
				),
			);
		if (invalidatesFrequency)
			setContractFrequencyReviewSelections(current =>
				current.filter(
					selection => String(selection.rowId) !== String(rowIdentity),
				),
			);
		clearContractFrequencyCreationCohorts(creationInvalidation?.cohortKeys);
		setPendingConfirmation(undefined);
	};

	const onDeleteRows = ids => {
		if (isBackgroundBlocked) return;
		const availableRowIds = new Set(
			reviewOrders.map(order => String(getImportRowIdentity(order))),
		);
		const deletedRowIds = new Set(
			(Array.isArray(ids) ? ids : [])
				.filter(id => id !== undefined && id !== null)
				.map(String)
				.filter(id => availableRowIds.has(id)),
		);
		if (!deletedRowIds.size) return;
		const deleteCount = deletedRowIds.size;
		if (
			typeof window.confirm !== 'function' ||
			!window.confirm(
				`Se eliminará ${
					deleteCount === 1 ? 'esta orden' : `${deleteCount} órdenes`
				} solo de este lote; no se eliminará de Synapse. ¿Deseas continuar?`,
			)
		)
			return;
		const invalidatedZonePlans = zoneCreationSelections.filter(selection =>
			(selection.targetRowIds || []).some(rowId =>
				deletedRowIds.has(String(rowId)),
			),
		);
		const invalidatedServicePlans = serviceCreationSelections.filter(
			selection =>
				(selection.targetRowIds || []).some(rowId =>
					deletedRowIds.has(String(rowId)),
				),
		);
		let updatedOrders = clearZoneCreationPlans(invalidatedZonePlans);
		if (invalidatedServicePlans.length)
			updatedOrders = updatedOrders.map(order =>
				invalidatedServicePlans.some(selection =>
					isOrderTargetedByServiceCreation(order, selection),
				)
					? markOrderForRevalidation(
							invalidatedServicePlans.reduce(
								(restored, selection) =>
									setServiceCreationSelected({
										order: restored,
										selection,
										selected: false,
									}),
								order,
							),
						)
					: order,
			);
		const hasFrequencyReviewCapabilities =
			contractFrequencyReviewSelections.length > 0 ||
			contractFrequencyCreationRequests.length > 0 ||
			contractFrequencyCreationSelections.length > 0 ||
			updatedOrders.some(order => {
				const frequency = getContractFrequencyPresentation(order);
				const creation = order?.importDiagnostics?.frequencyCreation;
				return (
					frequency.mutable === true ||
					frequency.candidates.length > 0 ||
					Boolean(creation && typeof creation === 'object')
				);
			});
		if (hasFrequencyReviewCapabilities)
			updatedOrders = updatedOrders.map(order =>
				deletedRowIds.has(String(getImportRowIdentity(order)))
					? order
					: markOrderForRevalidation(
							invalidateContractFrequencyCreationCapabilities(
								invalidateContractFrequencyCapabilities(order),
							),
						),
			);
		if (updatedOrders.some((order, index) => order !== reviewOrders[index]))
			onSetState({ orders: updatedOrders });
		if (invalidatedZonePlans.length)
			setZoneCreationSelections(current =>
				current.filter(selection => !invalidatedZonePlans.includes(selection)),
			);
		if (invalidatedServicePlans.length)
			setServiceCreationSelections(current =>
				current.filter(
					selection => !invalidatedServicePlans.includes(selection),
				),
			);
		if (serviceReviewSelections.length)
			setServiceReviewSelections(current =>
				current.filter(
					selection => !deletedRowIds.has(String(selection.rowId)),
				),
			);
		if (assignmentReviewSelections.length)
			setAssignmentReviewSelections(current =>
				current.filter(
					selection => !deletedRowIds.has(String(selection.rowId)),
				),
			);
		if (contractFrequencyReviewSelections.length)
			setContractFrequencyReviewSelections([]);
		if (contractFrequencyCreationRequests.length)
			setContractFrequencyCreationRequests([]);
		if (contractFrequencyCreationSelections.length)
			setContractFrequencyCreationSelections([]);
		if (fieldReviewSelections.length)
			setFieldReviewSelections(current =>
				current.filter(
					selection => !deletedRowIds.has(String(selection.rowId)),
				),
			);
		if (fieldReviewSnapshot.length)
			setFieldReviewSnapshot(current =>
				current.filter(proof => !deletedRowIds.has(String(proof.rowId))),
			);
		setSelectedBulkRowIds(current =>
			current.filter(rowId => !deletedRowIds.has(String(rowId))),
		);
		setPendingConfirmation(undefined);
		onDeleteRow(Array.from(deletedRowIds));
	};

	const fieldActions = {
		ids: {
			delete: {
				onChange: onDeleteRows,
			},
		},
		types: {},
	};
	['string', 'int', 'decimal', 'date', 'datetime', 'time', 'boolean'].forEach(
		type => {
			fieldActions.types[type] = { onChange: onFieldTypeChange };
		},
	);

	if (assignedTechIdField)
		fieldActions.ids[assignedTechIdField.id] = {
			onChange: onChangeExpertHandler,
		};
	if (serviceIdField)
		fieldActions.ids[serviceIdField.id] = {
			onChange: onChangeServiceHandler,
			preserveExistingImportService: true,
		};
	if (contractFrequencyIdField?.show === true)
		fieldActions.ids[contractFrequencyIdField.id] = {
			onChange: onApplyContractFrequency,
		};
	if (cityIdField && townshipIdField) {
		zoneFields.forEach(zoneIdField => {
			fieldActions.ids[zoneIdField.id] = {
				onChange: args =>
					onChangeZoneHandler({ ...args, fieldId: zoneIdField.id }),
			};
		});
	}

	return (
		<Drawer
			key='importPreviewDrawer'
			className={`${styles.importReviewDrawer} animated fadeIn fastest`}
			style={importReviewThemeStyle}
			headerBackgroundColor='var(--import-review-background)'
			headerColor='var(--import-review-text)'
			height='100%'
			width='100%'
			placement='top'
			closable={false}
			visible={true}
			title={
				<HeaderPreview
					themeStyle={importReviewThemeStyle}
					themeProfile={themeProfile}
					onContinue={onCreateOrders}
					onCancel={onCancelImport}
					onValidate={onValidateChanges}
					isValidating={isValidating}
					isSubmitting={isSubmitting}
					isInteractionBlocked={isBulkConfirmationPending}
					needsRevalidation={hasUnvalidatedChanges}
					orderCount={reviewOrders.length}
					blockingCount={blockingCount}
					autoCorrectedCount={autoCorrectedCount}
					zoneCoverageChangeCount={zoneCoverageChangeCount}
					zoneCreationCount={zoneCreationSummary.zoneCount}
					zoneCreationAffectedOrderCount={
						zoneCreationSummary.affectedOrderCount
					}
					contractFrequencyCreationCount={
						contractFrequencyCreationSummary.frequencyCount
					}
					contractFrequencyCreationAffectedOrderCount={
						contractFrequencyCreationSummary.affectedOrderCount
					}
					assignmentConfirmationRequired={
						hasPersistentAssignmentChallenge ||
						(isPendingSnapshotVisible &&
							pendingConfirmation?.assignmentReviewRequired === true)
					}
				/>
			}
		>
			<ImportReviewWorkspace
				themeStyle={importReviewThemeStyle}
				themeProfile={themeProfile}
				importReviewError={importReviewError}
				orders={reviewOrders}
				fields={fields}
				templateValidatorFields={templateValidatorFields}
				locationFields={locationFields}
				onApplyCandidate={onChangeZoneHandler}
				onSelectZoneCreation={onSelectZoneCreation}
				onUndoZoneCreation={onUndoZoneCreation}
				onApplyService={onChangeServiceHandler}
				onStageLegacyService={onStageLegacyService}
				onUndoServiceSelection={onUndoServiceSelection}
				onRequestServiceCreation={onRequestServiceCreation}
				onSelectServiceCreation={onSelectServiceCreation}
				onUndoServiceCreation={onUndoServiceCreation}
				manualZoneSelector={ZoneId}
				manualServiceSelector={ServiceId}
				frequencySelector={ContractFrequencyId}
				assignmentSelector={AssignedTechId}
				assignmentReviewSelections={assignmentReviewSelections}
				onStageAssignmentSelection={onStageAssignmentSelection}
				onApplyFrequency={onApplyContractFrequency}
				onRequestFrequencyCreation={onRequestFrequencyCreation}
				onSelectFrequencyCreation={onSelectFrequencyCreation}
				onUndoFrequencyCreation={onUndoFrequencyCreation}
				onDiscardFrequencyCreationRequest={onDiscardFrequencyCreationRequest}
				frequencyCreationRequests={contractFrequencyCreationRequests}
				frequencyCreationSelections={contractFrequencyCreationSelections}
				fieldReviewSelections={fieldReviewSelections}
				onApplyFieldReview={onApplyFieldReview}
				onUndoFieldReview={onUndoFieldReview}
				onDeleteRows={onDeleteRows}
				serviceCreationSelections={serviceCreationSelections}
				zoneCreationSelections={zoneCreationSelections}
				zoneCreationSummary={zoneCreationSummary}
				selectedBulkRowIds={selectedBulkRowIds}
				onSelectedBulkRowIdsChange={setSelectedBulkRowIds}
				onApplyBulkAssignment={onApplyBulkAssignment}
				onApplyBulkService={onApplyBulkService}
				onApplyBulkZone={onApplyBulkZone}
				onApplyBulkFrequency={onApplyBulkFrequency}
				isBusy={isBusy}
				isInteractionBlocked={isBulkConfirmationPending}
				onBulkConfirmationPendingChange={setIsBulkConfirmationPending}
			>
				<TablePreview
					previewOrders={previewOrders}
					fields={fields}
					fieldActions={fieldActions}
					isBusy={isBusy}
					isInteractionBlocked={isBulkConfirmationPending}
					selectedRowIds={selectedBulkRowIds}
					onSelectedRowIdsChange={setSelectedBulkRowIds}
				/>
			</ImportReviewWorkspace>
		</Drawer>
	);
};

export default ImportPreview;
